import { Injectable, ApplicationRef, NgZone, ElementRef } from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import * as moment from 'moment';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Location } from '@angular/common';
import { BreakpointObserver } from '@angular/cdk/layout';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { SwUpdate } from '@angular/service-worker';

import { Config } from '../config';
import { SnackBarFromComponentComponent } from '../components/snack-bar-from-component/snack-bar-from-component.component';
import { first, map } from 'rxjs/operators';

// Models & Data
import { navData } from '../data/nav-data';
import { Language } from '../model/language';
import { User, Navigation, NavigationChild, CustomMenuItem } from '../model/user';
import { CookieSessionLocalStorage } from '../model/system';
import {
  DynamicScript,
  DataProp,
  dynamicScripts,
  renderedScriptSrc,
  DynamicScriptTargetTypes,
} from '../data/dynamic-script-data';

import { culture, roadRisk, eLearning, roadRiskFlt } from '../data/course';
import { readyTranslations } from '../../assets/vrm-language-scripts/ready-translations';
import { LicenceCategory } from '../model/LicenceCategory';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { dashboardMenuAccessMap } from '../model/dashboards.model';

export enum DaysOfTheWeekEnum {
  sun,
  mon,
  tue,
  wed,
  thu,
  fri,
  sat,
}

export enum CoachingInterventions {
  oneToOne = 'OneToOne',
  pccReview = 'PccReview',
  driverImprovementReview = 'DriverImprovementReview',
  amzlWeeklyDriverIndexCoach = 'OneWeekFICO_LT500',
  amzlTwoWeeklyDriverIndexCoach = 'TwoWeeksFICO_LT710',
  driverIndexProCoach = 'DriverIndexProCoach',
  oneToOneProCoach = 'OneToOneProCoach',
  pccReviewProCoach = 'PccReviewProCoach',
  virtualSafetyDiscussion = 'VirtualSafetyDiscussion',
}

export interface PageName {
  page: string;
  isCommonReport?: boolean;
  isDashboardGroup?: boolean;
  isVrmAnalyticsDashboard?: boolean;
}

export interface ReportDate {
  period: string;
  startPeriod: string;
  endPeriod: string;
  hideCalendar?: boolean;
}

interface PageDatePeriod {
  date?: ReportDate;
  dateComparison?: { period: string; previousPeriod: string; currentPeriod: string };
}

interface ReportResourceLocation {
  endpoint: string;
  commonReportPath?: string;
  navigationData?: Navigation | NavigationChild;
}

export interface PageInterfaceSettings {
  hasMainNav?: boolean;
  hasMainHeader?: boolean;
  hasTitleHeader?: boolean;
  hasTranslationSwitch?: boolean;
  hasMoreOptions?: boolean;
  forceLocalPageTitle?: boolean;
}

interface PageHeader {
  // NOTE: All of them must be set by each instance
  pageTitle: string;
  canSearch: boolean;
  isDashboard: boolean;
  isReportPage: boolean;
  pageUiSettings?: PageInterfaceSettings;
}

export type DataExportExt = 'xlsx' | 'csv';

export enum FileExtensionEnum {
  excel = 'xlsx',
  csv = 'csv',
}

export type SsrDownloadType = 'filteredData' | 'allData';

export enum SsrDownloadTypeEnum {
  currentData = 'currentData',
  filteredData = 'filteredData',
  allData = 'allData',
}

export interface PromptDialogConfig {
  title?: string;
  titleDescription?: string;
  body?: string;
  actionButtonText?: string;
  cancelButtonText?: string;
  hideHeaderCloseButton?: boolean;
  showQueryProgress?: boolean;
  determinateProgressPerc?: number;
  disableCancelWhenQuerying?: boolean;
}

export interface VrmFileIcon {
  icon: string;
  color?: string;
}

type LangCodeType = 'ISO' | 'Material';

export enum LangCodeTypeEnum {
  iso = 'ISO',
  material = 'Material',
}

export type RatingToTextType = 'highMediumLowNa' | 'poorAverageGoodNa' | 'completeIncompleteNotStartedNa';

export enum NumericRatingToTextEnum {
  highMediumLowNa = 'highMediumLowNa',
  poorAverageGoodNa = 'poorAverageGoodNa',
  completeIncompleteNotStartedNa = 'completeIncompleteNotStartedNa',
}

export interface DashboardGroup {
  endpoint: string;
  dashboards: string[];
}

export const dashboardGroups: { [key: string]: DashboardGroup } = {
  main: {
    endpoint: '',
    dashboards: ['main'],
  },
  vrmUKDashboard: {
    endpoint: 'vrm_uk_dashboard',
    dashboards: ['vrmUKDashboard'],
  },
  mvrDashboard: {
    endpoint: 'mvr_dashboard',
    dashboards: ['mainMVR'],
  },
  mentorDashboard: {
    endpoint: 'mentor_dashboard',
    dashboards: ['mentorDashboard'],
  },
  coachingDashboard: {
    endpoint: 'coaching_dashboard',
    dashboards: ['coachingDashboard'],
  },
  licenceCheckDashboard: {
    endpoint: 'licence_check_dashboard',
    dashboards: ['licenceCheckDashboard'],
  },
  mentorTrendsDashboard: {
    endpoint: 'mentor_trends_dashboard',
    dashboards: ['mentorTrendsDashboard'],
  },
};

@Injectable({ providedIn: 'root' })
export class CommonService {
  imageRoot: string = Config.assetCdnRoot + 'images/';
  companiesLogoRoot: string = Config.assetCdnRoot + 'images/companies/';
  mentorAssetRoot: string = Config.assetCdnRoot + 'images/mentor/';

  private licenceCategories: LicenceCategory[];

  private _reportGridDefult = {
    defaultHeight: 33,
  };
  private _uploadBatchSize: number = 1;
  public get uploadBatchSize(): number {
    return this._uploadBatchSize;
  }

  private _sendActivationBatchSize: number = 10;
  public get sendActivationBatchSize(): number {
    return this._sendActivationBatchSize;
  }

  public get reportGridDefult() {
    return this._reportGridDefult;
  }

  public set reportGridDefult(value) {
    this._reportGridDefult = value;
  }

  pageHeader: PageHeader = {
    canSearch: false,
    pageTitle: '',
    isDashboard: false,
    isReportPage: false,
  };
  defaultPageUiSettings: PageInterfaceSettings = {
    hasMainNav: true,
    hasMainHeader: true,
    hasTitleHeader: true,
    hasTranslationSwitch: true,
    hasMoreOptions: true,
  };

  promptConfig: PromptDialogConfig;

  regEx = {
    underscoreAndCharsAfter: new RegExp(/_.*$/),
  };

  private _mainSidenavToggled = new BehaviorSubject(null);
  mainSidenavToggled$ = this._mainSidenavToggled.asObservable();

  private _navigationReady = new BehaviorSubject(null);
  navigationReady$ = this._navigationReady.asObservable();

  private _userProfileReady = new BehaviorSubject(null);
  userProfileReady$ = this._userProfileReady.asObservable();

  private _languageChangedWithoutReload = new BehaviorSubject('');
  languageChangedWithoutReload$ = this._languageChangedWithoutReload.asObservable();

  private _updateMenuBadgeCountSubject = new BehaviorSubject(null);
  updateMenuBadgeCount$ = this._updateMenuBadgeCountSubject.asObservable();

  private _languageSet = new BehaviorSubject<Language>(null);
  languageSet$ = this._languageSet.asObservable();

  private _pageChangedNavEnd = new BehaviorSubject(null);
  pageChangedNavEnd$ = this._pageChangedNavEnd.asObservable();

  /* ============================
	Shared Page-header events
  ============================= */
  private _headerSearchToggled = new BehaviorSubject(null);
  headerSearchToggled$ = this._headerSearchToggled.asObservable();

  private _headerSearchInputChanged = new BehaviorSubject(null);
  headerSearchInputChanged$ = this._headerSearchInputChanged.asObservable();

  private _downloadReportTriggered = new BehaviorSubject(null);
  downloadReportTriggered$ = this._downloadReportTriggered.asObservable();
  // -- End

  appVersion = Config.appVersion;
  appSettings = {
    waitInMsForInitialApiCalls: 2500,
    checkUpdateInHours: 3 * 60 * 60 * 1000, // <-- 3hrs
    defaultPage: Config.defaultPage,
    supportEmailAddress: 'support@virtualriskmanager.net',
    eDrivingWebsite: 'https://www.edriving.com',
    dialogHeaderGrey: '#9e9e9e',
  };

  selectedNav: Navigation;

  /* @deprecated Please use data/permission-levels.ts */
  permLevels = {
    TRUE: 'TRUE',
    FALSE: 'FALSE',
    view: 'view',
    viewEdit: 'view_edit',
    none: 'none',
    readonly: 'readonly',
    limited: 'limited',
    full: 'full',
    plus: 'plus',
    all: 'all',
    lite: 'lite',
    custom: 'custom',
    override: 'override',
    yes: 'yes',
    no: 'no',
  };
  tabVehicleTypes = {};

  passwordResetInProgress: boolean = false;
  passwordResetNameSpace: string = '';
  navigation: Array<Navigation> = [];
  localStorageKeys: Partial<CookieSessionLocalStorage>;
  commonReportTitle: string;
  navigatedReport: ReportResourceLocation;
  mainSidenavOpened: boolean = false;
  currentPageDateOrPeriod: PageDatePeriod = {};

  appStableSinceInitLoad$: Observable<boolean>;
  isOnline: boolean = true;
  isFullscreen: boolean = false;
  isDarkTheme: boolean = false;
  prodDataInUseInDev: boolean = false;
  productionInUse: boolean = false;
  prodOrPreProdInUse: boolean = false;
  appReady: boolean = false;
  appInitialLoad: boolean = true;
  hideIndeterminateProgress: boolean = true;
  elementIsFullscreen: boolean = false;
  currentTheme: string = 'grey-theme';
  naturalBgColor: '#fafafa';
  accentColor: string = '#795548';
  piiDisclaimer: string;
  copyright: string;
  vrmLanguages: Array<Language> = [];
  defaultLanguage: Language = {
    country: 'English',
    translatedLabel: 'English',
    code: 'en',
    isRtl: false,
  };
  selectedLanguage: Language;
  matLangCode: string;
  languageParam: string;
  reloadingLanguage: boolean = false;
  userProfile: User = {
    preferences: {},
  };
  paginationSizeOptions = [10000, 20000, 40000, 60000, 100000, 150000, 200000];
  dataRowCanExpand: boolean = false;

  carouselItemIndex: number = 0;
  carouselIntervalInterrupter: any;
  carouselItemIndicatorList: Array<number> = [];
  carouselPaused: boolean = false;

  provisioningErrors = {
    uploadErrorGeneral:
      "Errors found while uploading.Please check error below then click on 'Continue' button to try again.",
    invalidStations: 'Station(s) not found.',
    unknownServerError: 'Server Error. Please retry.',
    usernameOrEmailExists: 'User name or email exists',
    usernameExists: 'User name exists',
    emailExists: 'Email exists',
    invalidEmail: 'Invalid Email.',
    invalidEntry: 'Invalid Entry',
  };
  ficoScoreRatings: Array<any> = [];

  /** @deprecated Use mentor.helpers.ts > ficoScoreRiskLevels instead **/
  ficoScoreValues = {
    veryHighRisk: { from: 100, to: 499 },
    highRisk: { from: 500, to: 559 },
    mediumRisk: { from: 560, to: 709 },
    lowRisk: { from: 710, to: 799 },
    veryLowRisk: { from: 800, to: 850 },
  };

  mentorDrivingRatingValues = {
    highRisk: { min: 100, max: 559 },
    mediumRisk: { min: 560, max: 709 },
    lowRisk: { min: 710 },
  };

  highMediumLowRatingValues = {
    veryHigh: 4,
    high: 3,
    medium: 2,
    low: 1,
    // Less than 1 is NA
  };

  completeIncompleteNotStartedNa = {
    notStarted: 3,
    incomplete: 2,
    complete: 1,
    // Less than 1 is NA <-- Caters for null values
  };

  fileTypesToIcon = {
    pdf: { icon: 'far fa-file-pdf', color: '#ff0000' },
    mp4: { icon: 'far fa-file-video' },
    doc: { icon: 'far fa-file-word', color: ' #2b579a' },
    docx: { icon: 'far fa-file-word', color: ' #2b579a' },
  };

  readyTranslations = readyTranslations;

  navigationFilterTexts: any = {
    car: {
      id: 'car',
      name: 'Car',
      translatedName: this.readyTranslations.navigationFilter.Car,
    },
    flt: {
      id: 'flt',
      name: 'PIV',
      translatedName: this.readyTranslations.navigationFilter['PIV'],
    },
    '2w': {
      id: '2w',
      name: 'Two Wheeler',
      translatedName: this.readyTranslations.navigationFilter['Two Wheeler'],
    },
  };

  isMobileViewport: boolean = false;
  isTabletViewport: boolean = false;
  isPcViewport: boolean = false;
  isLargePcViewport: boolean = false;

  commonReports = 'commonreports';
  dashboards = 'dashboard';
  vrmAnalyticsDashboards = 'vrm-analytics-dashboard';

  constructor(
    private router: Router,
    private translate: TranslateService,
    private snackbar: MatSnackBar,
    private location: Location,
    private breakpointObserver: BreakpointObserver,
    private appRef: ApplicationRef,
    private zone: NgZone,
    private dateAdapter: DateAdapter<any>,
    private http: HttpClient,
    private appUpdate: SwUpdate
  ) {
    this.initialize();
  }

  // RxJs EVENT
  triggerMainSidenavToggledAsObservable(opened: boolean) {
    this._mainSidenavToggled.next(opened);
  }

  private triggerNavigationReadyObservable() {
    this._navigationReady.complete();
  }

  triggerUserProfileReadyObservable(userProfile: User) {
    this._userProfileReady.next(userProfile);
  }

  triggerLanguageChangedObservable(langCode: string) {
    this._languageChangedWithoutReload.next(langCode);
  }

  triggerPageChangedNavEndObservable() {
    this._pageChangedNavEnd.next(null);
  }

  triggerMenuBadgeCountUpdateObservable(targetBadge: string): void {
    this._updateMenuBadgeCountSubject.next(targetBadge);
  }

  /* ===============================
  Shared Page-header events triggers
  ================================= */
  triggerHeaderSearchToggledAsObservable(closed: boolean) {
    this._headerSearchToggled.next(closed);
  }

  triggerHeaderSearchInputChangedAsObservable(searchText: string) {
    this._headerSearchInputChanged.next(searchText);
  }

  triggerReportDownloadRequestAsObservable(extension: DataExportExt) {
    this._downloadReportTriggered.next(extension);
  }

  // -- End

  initialize(): void {
    /**
     * NOTE
     * The app is NOT stable if timeout / intervals are running
     * from start-up. Implementing a check via this observable
     * helps to know when all the time-related tasks are done.
     *
     * When checking that the app is stable, if there's a process that updates
     * the DOM, such process must be implemented to run within the Angular process.
     * This is because the stability check runs outside the Angular zone, hence
     * Change Detection will not be triggered.
     */
    this.appStableSinceInitLoad$ = this.appRef.isStable;

    this.productionInUse = environment.production;
    this.prodOrPreProdInUse = environment.production || environment.preProduction;

    this.copyright = `&copy; ${new Date().getFullYear()} eDriving LLC . `;

    this.localStorageKeys = {
      cookiesAccepted: this.applyPrefix('cookieAccepted'),
      language: this.applyPrefix('language'),
      vrmLanguageList: this.applyPrefix('vrmLanguageList'),
      isDarkTheme: this.applyPrefix('isDarkTheme'),
      currentTheme: this.applyPrefix('currentTheme'),
      accessTokenExpiration: this.applyPrefix('access_token_exp'),
      userAuthenticated: this.applyPrefix('user_authenticated'),
      userProfile: this.applyPrefix('userProfile'),
      selectedDriver: this.applyPrefix('selectedDriver'),
      selectedCasualReportRow: this.applyPrefix('casualReportRow'),
      pageTooltips: this.applyPrefix('pageTooltips'),
      pageTooltipPreviews: this.applyPrefix('pageTooltipPreviews'),
      cachedCommonReportPage: this.applyPrefix('cachedCommonReportPage'),
      gridFilterCache: this.applyPrefix('gridFilterCache'),
      activeQueryFilters: this.applyPrefix('activeQueryFilters'),
      gridCustomHighMedLow: this.applyPrefix('gridCustomHighMedLow'),
    };

    // Languages
    this.userProfile = JSON.parse(localStorage.getItem(this.localStorageKeys.userProfile));
    const lastSelectedLanguage = localStorage.getItem(this.localStorageKeys.language);
    if (this.userProfile) {
      this.setSelectedLanguage(this.userProfile['languageCode']);
    } else if (lastSelectedLanguage) {
      this.setSelectedLanguage(lastSelectedLanguage);
    } else {
      this.setSelectedLanguage('en');
    }

    this.tabVehicleTypes = {
      car: {
        id: 'car',
        name: 'Car',
        translatedName: this.readyTranslations.navigationFilter.Car,
      },
      flt: {
        id: 'flt',
        name: 'PIV',
        translatedName: this.readyTranslations.navigationFilter['PIV'],
      },
      '2w': {
        id: '2w',
        name: 'Two Wheeler',
        translatedName: this.readyTranslations.navigationFilter['Two Wheeler'],
      },
    };
  }

  // Helper methods
  getLanguageByCode(code: string): Language {
    let language = this.vrmLanguages.find((lang) => lang.code.toLowerCase() === code);

    if (!language) {
      language = this.vrmLanguages.find((lang) => lang.code.toLowerCase() === 'en');
    }

    return language;
  }

  // Helper methods
  getLanguageByCountry(country: string): Language {
    let language = this.vrmLanguages.find((lang) => lang.country.toLowerCase() === country);
    if (!language) {
      language = this.vrmLanguages.find((lang) => lang.country.toLowerCase() === 'english');
    }

    return language;
  }

  applyPrefix(suffix: string): string {
    return 'mis.vrm.eDriving.' + suffix;
  }

  applyUserProfile() {
    if (this.userProfile) {
      this.currentTheme = this.userProfile.color;
      const savedTheme = localStorage.getItem(this.localStorageKeys.currentTheme);
      if (savedTheme !== this.currentTheme) {
        localStorage.setItem(this.localStorageKeys.currentTheme, this.userProfile.color);
      }
    } else {
      console.warn(':: VRM Warning: User profile not found');
    }

    if (this.vrmLanguages && this.vrmLanguages.length) {
      this.setLanguage();
    } else {
      this.getLanguageList().subscribe({
        next: () => {
          this.setLanguage();
        },
      });
    }
  }

  private setLanguage() {
    let langCode: string;
    const lastSelectedLanguage = localStorage.getItem(this.localStorageKeys.language);

    if (this.userProfile && this.userProfile.languageCode) {
      langCode = this.userProfile.languageCode.toLowerCase();
    } else if (lastSelectedLanguage) {
      langCode = lastSelectedLanguage;
    } else {
      langCode = this.languageParam;
    }

    const selectedLanguage: Language = this.getLanguageByCode(langCode);
    this.setSelectedLanguage(langCode);

    this.processLanguageTranslations(selectedLanguage);
  }

  applyBreakpoint() {
    const breakpoint = 500;
    const mobileBreakpoint = breakpoint + 'px';
    const tabletBreakpoint = breakpoint + 200 + 'px';
    const pcBreakpoint = breakpoint + 400 + 'px';
    const largePcBreakpoint = breakpoint + 900 + 'px';

    this.isMobileViewport = this.breakpointObserver.isMatched(`(max-width: ${mobileBreakpoint})`);
    this.isTabletViewport = this.breakpointObserver.isMatched(`(max-width: ${tabletBreakpoint})`);

    this.isPcViewport = !this.breakpointObserver.isMatched(`(max-width: ${pcBreakpoint})`);
    this.isLargePcViewport = !this.breakpointObserver.isMatched(`(max-width: ${largePcBreakpoint})`);

    setTimeout(() => {
      if (this.isPcViewport) {
        this.mainSidenavOpened = true;
      } else {
        this.mainSidenavOpened = false;
      }
    });
  }

  /**
   * @param waitCallback If needed, ensures sidenav toggle is done and value
   * propagated before next action.
   */
  toggleMainSidenav(waitCallback?: () => void) {
    this.mainSidenavOpened = !this.mainSidenavOpened;

    setTimeout(() => {
      if (waitCallback) {
        waitCallback();
      }
    }, 60);
  }

  setPageHeader(
    pageTitle: string,
    canSearch: boolean = false,
    isReportPage: boolean = false,
    isDashboard: boolean = false,
    pageUiSettings: PageInterfaceSettings = this.defaultPageUiSettings
  ) {
    setTimeout(() => {
      if (!this.pageHeader.pageTitle || pageUiSettings.forceLocalPageTitle) {
        // NOTE: pageTitle is set in the getNavigatedReportData() method
        // to ensure the same navigation translated name is applied.
        // Only set here if not already set in the method mentioned above.
        this.pageHeader.pageTitle = pageTitle;
      }
      this.pageHeader.isReportPage = isReportPage;
      this.pageHeader.canSearch = canSearch;
      this.pageHeader.isDashboard = isDashboard;
      this.pageHeader.pageUiSettings = pageUiSettings;

      console.log('%c Page Header Properties', 'color: yellow', this.pageHeader);
    });

    this.awaitPageReadiness();
  }

  private awaitPageReadiness() {
    setTimeout(() => {
      this.appReady = true;
      this.appInitialLoad = false;
    }, this.appSettings.waitInMsForInitialApiCalls);
  }

  /**
   * @param message Shows translated 'Working...' if no message provided
   * @param action do not translate or provide action method for 'Close' or 'Refresh now' actions
   * @param actionMethod only provide when action is not 'Close' or 'Refresh now'
   */
  showToast(message?: string, duration?: number, action?: string, actionMethod?: () => void) {
    const actualMessage = message ? message : this.readyTranslations.messagesAndWarnings.working;
    const actualDuration = duration ? duration : 4000;

    let actionText = action;
    if (action) {
      if (action.toLowerCase().replace(/ /g, '') === 'refreshnow') {
        actionText = this.readyTranslations.generic.refresh;
      } else if (action.toLowerCase() === 'close') {
        actionText = this.readyTranslations.generic.close;
      }
    }

    this.snackbar
      .open(actualMessage, actionText, {
        duration: actualDuration,
      })
      .onAction()
      .subscribe({
        next: () => {
          if (actionMethod) {
            actionMethod();
          } else {
            if (action) {
              if (action.toLowerCase().replace(/ /g, '') === 'refreshnow') {
                this.reloadAndTryToApplyUpdates();
              }

              if (action.toLowerCase() === 'close') {
                this.snackbar.dismiss();
              }
            }
          }
        },
      });
  }

  /**
   * @param message Shows translated 'Working...' if no message provided
   * @param action do not translate or provide action method for 'Close' or 'Refresh now' actions
   * @param actionMethod only provide when action is not 'Close' or 'Refresh now'
   */
  showToastWithoutDuration(message?: string, action?: string, actionMethod?: () => void) {
    let actionText = action;
    if (action && action.toLowerCase().replace(/ /g, '') === 'refreshnow') {
      actionText = this.readyTranslations.generic.refresh;
    } else if (action && action.toLowerCase() === 'close') {
      actionText = this.readyTranslations.generic.close;
    }

    this.snackbar
      .open(message, actionText)
      .onAction()
      .subscribe({
        next: () => {
          if (actionMethod) {
            actionMethod();
          } else {
            if (action) {
              if (action.toLowerCase().replace(/ /g, '') === 'refreshnow') {
                this.reloadAndTryToApplyUpdates();
              }

              if (action.toLowerCase() === 'close') {
                this.snackbar.dismiss();
              }
            }
          }
        },
      });
  }

  closeCurrentToast() {
    this.snackbar.dismiss();
  }

  showTimeoutToastFromComponent(duration: number, timeoutInSeconds: number) {
    this.snackbar
      .openFromComponent(SnackBarFromComponentComponent, {
        duration: duration ? duration : 4000,
        data: {
          countdownFrom: timeoutInSeconds,
        },
      })
      .onAction()
      .subscribe({
        next: () => {
          this.snackbar.dismiss();
        },
      });
  }

  // Translations & Navigation
  processReadyTranslations() {
    // Dashboard
    this.translate.get('DASHBOARD').subscribe({
      next: (translation) => {
        const objectKeys = Object.keys(this.readyTranslations.dashboard);

        objectKeys.forEach((objKey) => {
          this.readyTranslations.dashboard[objKey] = translation[objKey]
            ? translation[objKey]
            : this.readyTranslations.dashboard[objKey];
        });
      },
    });

    this.translate.get('MAINSIDENAV').subscribe({
      next: (translation) => {
        for (const key in this.readyTranslations.mainSidenav) {
          if (key) {
            this.readyTranslations.mainSidenav[key] = translation[key]
              ? translation[key]
              : this.readyTranslations.mainSidenav[key];
          }
        }
      },
    });

    // Errors and warning ready translations
    this.translate.get('MESSAGESANDWARNINGS').subscribe({
      next: (translation) => {
        const objectKeys = Object.keys(this.readyTranslations.messagesAndWarnings);

        objectKeys.forEach((objKey) => {
          this.readyTranslations.messagesAndWarnings[objKey] = translation[objKey]
            ? translation[objKey]
            : this.readyTranslations.messagesAndWarnings[objKey];
        });
      },
    });

    // Generic ready translations
    this.translate.get('GENERIC').subscribe({
      next: (translation) => {
        const objectKeys = Object.keys(this.readyTranslations.generic);

        objectKeys.forEach((objKey) => {
          this.readyTranslations.generic[objKey] = translation[objKey]
            ? translation[objKey]
            : this.readyTranslations.generic[objKey];
        });
      },
    });

    // Guide FAQ
    this.translate.get('GUIDEFAQ').subscribe({
      next: (translation) => {
        for (const key in this.readyTranslations.guideFaq) {
          if (key) {
            this.readyTranslations.guideFaq[key] = translation[key]
              ? translation[key]
              : this.readyTranslations.guideFaq[key];
          }
        }
      },
    });

    // Login
    this.translate.get('LOGIN').subscribe({
      next: (translation) => {
        for (const key in this.readyTranslations.login) {
          if (key) {
            this.readyTranslations.login[key] = translation[key] ? translation[key] : this.readyTranslations.login[key];
          }
        }
      },
    });

    // Security
    this.translate.get('SECURITY').subscribe({
      next: (translation) => {
        for (const key in this.readyTranslations.security) {
          if (key) {
            this.readyTranslations.security[key] = translation[key]
              ? translation[key]
              : this.readyTranslations.security[key];
          }
        }
      },
    });

    // Custom Tooltips
    this.translate.get('CUSTOMTOOLTIPS').subscribe({
      next: (translation) => {
        for (const key in this.readyTranslations.customTooltips) {
          if (key) {
            this.readyTranslations.customTooltips[key] = translation[key]
              ? translation[key]
              : this.readyTranslations.customTooltips[key];
          }
        }
      },
    });

    this.translate.get('REPORTCOLUMNS').subscribe({
      next: (translation) => {
        for (const key in this.readyTranslations.reportColumns) {
          if (key) {
            this.readyTranslations.reportColumns[key] = translation[key]
              ? translation[key]
              : this.readyTranslations.reportColumns[key];
          }
        }
      },
    });

    // Courses
    this.translate.get('COURSES').subscribe({
      next: (translation) => {
        for (const key in this.readyTranslations.courses) {
          if (key) {
            this.readyTranslations.courses[key] = translation[key] ? translation[key] : this.readyTranslations[key];
          }
        }
      },
    });

    // Form and validation
    this.translate.get('FORM').subscribe({
      next: (translation) => {
        const objectKeys = Object.keys(this.readyTranslations.form);

        objectKeys.forEach((objKey) => {
          this.readyTranslations.form[objKey] = translation[objKey]
            ? translation[objKey]
            : this.readyTranslations.form[objKey];
        });
      },
    });

    this.translate.get('USER').subscribe((translation) => {
      const objectKeys = Object.keys(this.readyTranslations.user);

      objectKeys.forEach((objKey) => {
        this.readyTranslations.user[objKey] = translation[objKey]
          ? translation[objKey]
          : this.readyTranslations.user[objKey];
      });
    });

    // Order MVR
    this.translate.get('ORDERMVR').subscribe({
      next: (translation) => {
        const objectKeys = Object.keys(this.readyTranslations.orderMvr);

        objectKeys.forEach((objKey) => {
          this.readyTranslations.orderMvr[objKey] = translation[objKey]
            ? translation[objKey]
            : this.readyTranslations.orderMvr[objKey];
        });
      },
    });
    // USER CSV
    this.translate.get('USER_CSV').subscribe({
      next: (translation) => {
        const objectKeys = Object.keys(this.readyTranslations.userCsv);
        objectKeys.forEach((objKey) => {
          this.readyTranslations.userCsv[objKey] = translation[objKey]
            ? translation[objKey]
            : this.readyTranslations.userCsv[objKey];
        });
      },
    });

    // Resolve Alert
    this.translate.get('ALERTS').subscribe({
      next: (translation) => {
        const objectKeys = Object.keys(this.readyTranslations.alerts);

        objectKeys.forEach((objKey) => {
          this.readyTranslations.alerts[objKey] = translation[objKey]
            ? translation[objKey]
            : this.readyTranslations.alerts[objKey];
        });
      },
    });

    // Resolve Licence Checking
    this.translate.get('LICENCE_CHECKING').subscribe({
      next: (translation) => {
        const objectKeys = Object.keys(this.readyTranslations.licenceChecking);

        objectKeys.forEach((objKey) => {
          this.readyTranslations.licenceChecking[objKey] = translation[objKey]
            ? translation[objKey]
            : this.readyTranslations.licenceChecking[objKey];
        });
      },
    });

    // Report data
    this.translate.get('REPORTDATA').subscribe({
      next: (translation) => {
        const objectKeys = Object.keys(this.readyTranslations.reportData);

        objectKeys.forEach((objKey) => {
          this.readyTranslations.reportData[objKey] = translation[objKey]
            ? translation[objKey]
            : this.readyTranslations.reportData[objKey];
        });

        // FICO Score Ratings
        this.ficoScoreRatings = [
          {
            scoreMargin: `${this.ficoScoreValues.veryHighRisk.from} - ${this.ficoScoreValues.veryHighRisk.to}`,
            rating: translation.veryHighRisk ? translation.veryHighRisk : 'Very High Risk',
            color: 'red',
            class: 'vrm-red-label',
          },
          {
            scoreMargin: `${this.ficoScoreValues.highRisk.from} - ${this.ficoScoreValues.highRisk.to}`,
            rating: translation.highRisk ? translation.highRisk : 'High Risk',
            color: 'red',
            class: 'vrm-red-label',
          },
          {
            scoreMargin: `${this.ficoScoreValues.mediumRisk.from} - ${this.ficoScoreValues.mediumRisk.to}`,
            rating: translation.mediumRisk ? translation.mediumRisk : 'Medium Risk',
            color: 'yellow',
            class: 'vrm-yellow-label',
          },
          {
            scoreMargin: `${this.ficoScoreValues.lowRisk.from} - ${this.ficoScoreValues.lowRisk.to}`,
            rating: translation.lowRisk ? translation.lowRisk : 'Low Risk',
            color: 'green',
            class: 'vrm-green-label',
          },
          {
            scoreMargin: `${this.ficoScoreValues.veryLowRisk.from} - ${this.ficoScoreValues.veryLowRisk.to}`,
            rating: translation.veryLowRisk ? translation.veryLowRisk : 'Very Low Risk',
            color: 'green',
            class: 'vrm-green-label',
          },
        ];

        console.log('%c Ready Translations Processed', 'color: pink', this.readyTranslations);
      },
    });

    // Dashboard filters
    this.translate.get('DASHBOARDFILTERS').subscribe({
      next: (translation) => {
        const objectKeys = Object.keys(this.readyTranslations.dashboardFilters);

        objectKeys.forEach((objKey) => {
          this.readyTranslations.dashboardFilters[objKey] = translation[objKey]
            ? translation[objKey]
            : this.readyTranslations.dashboardFilters[objKey];
        });
      },
    });
  }

  public convertLanguageCode(vrmLangCode: string, codeType: LangCodeType): string {
    let convertedLangCode: string;

    const materialLangCodeMatch = {
      jp: 'ja-JP',
      kr: 'ko',
      cn: 'zh-CN',
      zh: 'zh-HK',
      brpt: 'pt-BR',
      it: 'it-IT',
      ara: 'ar',
    };

    const isoLangCodeMatch = {
      jp: 'ja',
      kr: 'ko',
      cn: 'zh',
      brpt: 'pt',
      gr: 'el',
    };

    if (codeType === LangCodeTypeEnum.iso) {
      convertedLangCode = isoLangCodeMatch[vrmLangCode];
    }

    if (codeType === LangCodeTypeEnum.material) {
      convertedLangCode = materialLangCodeMatch[vrmLangCode];
    }

    return convertedLangCode ? convertedLangCode : vrmLangCode;
  }

  /**
   * https://momentjscom.readthedocs.io/en/latest/moment/07-customization/16-dow-doy/
   * calculates first week of year required by moment
   * dayOfWeek: first day of week
   * janX: first day of January that must belongs to first week of year.
   */
  private calculateMomentDoy(dayOfWeek: number, janX: number): number {
    return 7 + dayOfWeek - janX;
  }

  processLanguageTranslations(language: Language): void {
    this.selectedLanguage = language;
    this.translate.use(language.code.toLowerCase()).subscribe({
      next: () => {
        let translationCode = language.code.toLowerCase();
        // NOTE: Wrong translations have been spotted in the following languages
        // when translated by moment. Fallback to English to pick-up VRM's Local
        // translations instead
        const momentTranslationExemptions = ['it'];

        if (momentTranslationExemptions.includes(translationCode)) {
          translationCode = 'en';
        }

        this.matLangCode = this.convertLanguageCode(language.code.toLowerCase(), LangCodeTypeEnum.material);
        this.matLangCode = this.matLangCode ? this.matLangCode : language.code.toLowerCase();
        this.dateAdapter.setLocale(this.matLangCode);
        // Force Day of the week to Sunday for all Languages
        this.dateAdapter.getFirstDayOfWeek = () => {
          return DaysOfTheWeekEnum.sun;
        };
        moment.updateLocale(translationCode, {
          week: { dow: 0, doy: this.calculateMomentDoy(0, 1) },
        });

        localStorage.setItem(this.localStorageKeys.language, language.code.toLowerCase());

        this.processReadyTranslations();
        this.processMainNavTranslations();

        this.triggerLanguageChangedObservable(language.code);
      },
    });
  }

  getConciseEndpointAndCommonReportNavPath(commonReportEndpoint: string): ReportResourceLocation {
    let endpoint = commonReportEndpoint.replace(/\/reports\//, '');
    if (endpoint.startsWith(`/`)) {
      endpoint = endpoint.replace(/\//, '');
    }

    return {
      endpoint: endpoint,
      commonReportPath: `#/commonreports/${endpoint.replace(/\//, `-`)}`,
    };
  }

  getDashboardGroupUrl(groupId: string): string {
    let dashboardGroupUrl = `#/vrm-analytics-dashboard/${dashboardGroups[groupId].endpoint}`;

    if (groupId === 'main') {
      dashboardGroupUrl = '#/vrm-analytics-dashboard';
    }

    return dashboardGroupUrl;
  }

  processMainNavTranslations(): void {
    this.translate.get('MAINSIDENAV').subscribe({
      next: (translation) => {
        this.navigation = [
          // DSP Performance
          {
            ...navData.dspPerformance,
            name: navData.dspPerformance.idName,
            fallbackName: navData.dspPerformance.idName,
            reportPageEndpoint: navData.dspPerformance.endpoint,
            url: this.getConciseEndpointAndCommonReportNavPath(navData.dspPerformance.endpoint).commonReportPath,
            translatedName: translation.dspPerformance,
            hidden: true,
          },

          // DSP DER
          {
            ...navData.dspDer,
            name: navData.dspDer.idName,
            fallbackName: navData.dspDer.idName,
            translatedName: translation.dspDer,
            reportPageEndpoint: navData.dspDer.endpoint,
            url: this.getConciseEndpointAndCommonReportNavPath(navData.dspDer.endpoint).commonReportPath,
            hidden: true,
          },

          // Status Report
          {
            ...navData.statusReport,
            name: navData.statusReport.idName,
            fallbackName: navData.statusReport.idName,
            translatedName: translation.analytics,
            children: [
              {
                ...navData.vrmAnalyticsDashboard,
                name: navData.vrmAnalyticsDashboard.idName,
                fallbackName: navData.vrmAnalyticsDashboard.idName,
                translatedName: `VRM ${translation.dashboard}`,
                url: this.getDashboardGroupUrl(navData.vrmAnalyticsDashboard.dashboardGroupId),
                dashboardGroup: navData.vrmAnalyticsDashboard.dashboardGroupId,
                isVrmAnalyticsDashboard: true,
                hidden: true,
              },
              {
                ...navData.vrmAnalyticsVrmUKDashboard,
                name: navData.vrmAnalyticsVrmUKDashboard.idName,
                fallbackName: navData.vrmAnalyticsVrmUKDashboard.idName,
                translatedName: translation.vrmUKDashboard,
                url: this.getDashboardGroupUrl(navData.vrmAnalyticsVrmUKDashboard.dashboardGroupId),
                dashboardGroup: navData.vrmAnalyticsVrmUKDashboard.dashboardGroupId,
                isVrmAnalyticsDashboard: true,
                hidden: true,
              },
              {
                ...navData.vrmAnalyticsMvrDashboard,
                name: navData.vrmAnalyticsMvrDashboard.idName,
                fallbackName: navData.vrmAnalyticsMvrDashboard.idName,
                translatedName: translation.mvrDashboard,
                url: this.getDashboardGroupUrl(navData.vrmAnalyticsMvrDashboard.dashboardGroupId),
                dashboardGroup: navData.vrmAnalyticsMvrDashboard.dashboardGroupId,
                isVrmAnalyticsDashboard: true,
                hidden: true,
              },
              {
                ...navData.vrmAnalyticsMentorDashboard,
                name: navData.vrmAnalyticsMentorDashboard.idName,
                fallbackName: navData.vrmAnalyticsMentorDashboard.idName,
                translatedName: translation.mentorDashboard,
                url: this.getDashboardGroupUrl(navData.vrmAnalyticsMentorDashboard.dashboardGroupId),
                dashboardGroup: navData.vrmAnalyticsMentorDashboard.dashboardGroupId,
                isVrmAnalyticsDashboard: true,
                hidden: true,
              },
              {
                ...navData.vrmAnalyticsCoachingDashboard,
                name: navData.vrmAnalyticsCoachingDashboard.idName,
                fallbackName: navData.vrmAnalyticsCoachingDashboard.idName,
                translatedName: translation.coachingDashboard,
                url: this.getDashboardGroupUrl(navData.vrmAnalyticsCoachingDashboard.dashboardGroupId),
                dashboardGroup: navData.vrmAnalyticsCoachingDashboard.dashboardGroupId,
                isVrmAnalyticsDashboard: true,
                hidden: true,
              },
              {
                ...navData.vrmAnalyticsLicenceCheckDashboard,
                name: navData.vrmAnalyticsLicenceCheckDashboard.idName,
                fallbackName: navData.vrmAnalyticsLicenceCheckDashboard.idName,
                translatedName: translation.licenceCheckDashboard,
                url: this.getDashboardGroupUrl(navData.vrmAnalyticsLicenceCheckDashboard.dashboardGroupId),
                dashboardGroup: navData.vrmAnalyticsLicenceCheckDashboard.dashboardGroupId,
                isVrmAnalyticsDashboard: true,
                hidden: true,
              },
              {
                ...navData.vrmAnalyticsMentorTrendsDashboard,
                name: navData.vrmAnalyticsMentorTrendsDashboard.idName,
                fallbackName: navData.vrmAnalyticsMentorTrendsDashboard.idName,
                translatedName: translation.mentorTrendsDashboard,
                url: this.getDashboardGroupUrl(navData.vrmAnalyticsMentorTrendsDashboard.dashboardGroupId),
                dashboardGroup: navData.vrmAnalyticsMentorTrendsDashboard.dashboardGroupId,
                isVrmAnalyticsDashboard: true,
                hidden: true,
              },
              {
                ...navData.customReports,
                name: navData.customReports.idName,
                fallbackName: navData.customReports.idName,
                translatedName: translation.customReports,
                reportPageEndpoint: navData.customReports.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.customReports.endpoint).commonReportPath,
                hidden: true,
              },
            ],
            hidden: true,
          },

          // VRM Alerts
          {
            ...navData.vrmAlerts,
            id: navData.vrmAlerts.id,
            name: navData.vrmAlerts.idName,
            fallbackName: navData.vrmAlerts.idName,
            reportPageEndpoint: navData.vrmAlerts.endpoint,
            url: this.getConciseEndpointAndCommonReportNavPath(navData.vrmAlerts.endpoint).commonReportPath,
            translatedName: translation.vrmAlerts,
            hidden: true,
            notificationsBadge: {
              display: true,
              tooltip: 'ALERTS.totalUnresolvedAlerts',
            },
          },

          // Mentor TSP Summary
          {
            ...navData.mentorTspSummary,
            id: navData.mentorTspSummary.id,
            name: navData.mentorTspSummary.idName,
            fallbackName: navData.mentorTspSummary.idName,
            reportPageEndpoint: navData.mentorTspSummary.endpoint,
            url: this.getConciseEndpointAndCommonReportNavPath(navData.mentorTspSummary.endpoint).commonReportPath,
            translatedName: translation.tspReport,
            hidden: true,
          },

          // Driver performance reports
          {
            ...navData.driverPerformance,
            name: navData.driverPerformance.idName,
            fallbackName: navData.driverPerformance.idName,
            translatedName: translation.driverPerformance,
            hidden: true,
            children: [
              {
                ...navData.driverPerformanceReport,
                name: navData.driverPerformanceReport.idName,
                fallbackName: navData.driverPerformanceReport.idName,
                reportPageEndpoint: navData.driverPerformanceReport.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.driverPerformanceReport.endpoint)
                  .commonReportPath,
                translatedName: translation.driverPerformanceReport,
                hidden: true,
              },
              {
                ...navData.driverPerformanceComparison,
                name: navData.driverPerformanceComparison.idName,
                fallbackName: navData.driverPerformanceComparison.idName,
                reportPageEndpoint: navData.driverPerformanceComparison.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.driverPerformanceComparison.endpoint)
                  .commonReportPath,
                translatedName: translation.deltaComparison,
                hidden: true,
              },
              {
                ...navData.mentorUtilisationReport,
                name: navData.mentorUtilisationReport.idName,
                fallbackName: navData.mentorUtilisationReport.idName,
                reportPageEndpoint: navData.mentorUtilisationReport.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.mentorUtilisationReport.endpoint)
                  .commonReportPath,
                translatedName: translation.mentorUtilisation,
                hidden: true,
              },
            ],
          },
          // Station Performance reports
          {
            ...navData.stationReport,
            name: navData.stationReport.idName,
            fallbackName: navData.stationReport.idName,
            translatedName: translation.driverDspReport,
            hidden: true,
            children: [
              {
                ...navData.stationReportReport,
                name: navData.stationReportReport.idName,
                fallbackName: navData.stationReportReport.idName,
                reportPageEndpoint: navData.stationReportReport.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.stationReportReport.endpoint)
                  .commonReportPath,
                translatedName: translation.stationPerformanceReport,
              },
              {
                ...navData.stationReportComparison,
                name: navData.stationReportComparison.idName,
                fallbackName: navData.stationReportComparison.idName,
                reportPageEndpoint: navData.stationReportComparison.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.stationReportComparison.endpoint)
                  .commonReportPath,
                translatedName: translation.deltaComparison,
              },
            ],
          },

          // Driver Activity Reporting
          {
            ...navData.driverActivityReporting,
            name: navData.driverActivityReporting.idName,
            fallbackName: navData.driverActivityReporting.idName,
            translatedName: translation.driverActivityReporting,
            children: [
              {
                ...navData.driverActivitySummaryReport,
                name: navData.driverActivitySummaryReport.idName,
                fallbackName: navData.driverActivitySummaryReport.idName,
                translatedName: translation.driverActivitySummary,
                details: translation.driverActivityReporting,
                reportPageEndpoint: navData.driverActivitySummaryReport.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.driverActivitySummaryReport.endpoint)
                  .commonReportPath,
                hidden: true,
              },
              {
                ...navData.riskCoachDriverActivityDetail,
                name: navData.riskCoachDriverActivityDetail.idName,
                fallbackName: navData.riskCoachDriverActivityDetail.idName,
                translatedName: translation.riskCoachDriverActivityDetail,
                hidden: true,
                reportPageEndpoint: navData.riskCoachDriverActivityDetail.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.riskCoachDriverActivityDetail.endpoint)
                  .commonReportPath,
              },
              {
                ...navData.vrmCoursActivity,
                name: navData.vrmCourseActivity.idName,
                fallbackName: navData.vrmCourseActivity.idName,
                translatedName: translation.vrmCourseActivity,
                reportPageEndpoint: navData.vrmCourseActivity.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.vrmCourseActivity.endpoint).commonReportPath,
                hidden: true,
              },
              {
                ...navData.driverActivityCompare,
                name: navData.driverActivityCompare.idName,
                fallbackName: navData.driverActivityCompare.idName,
                translatedName: translation.driverActivityCompare,
                details: translation.driverActivityReporting,
                reportPageEndpoint: navData.driverActivityCompare.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.driverActivityCompare.endpoint)
                  .commonReportPath,
                hidden: true,
              },
            ],
            hidden: true,
          },

          // External Training
          {
            ...navData.driverExternalTraining,
            name: navData.driverExternalTraining.idName,
            fallbackName: navData.driverExternalTraining.idName,
            translatedName: translation.driverExternalTrainingReport,
            children: [
              {
                ...navData.driverExternalTrainingReport,
                name: navData.driverExternalTrainingReport.idName,
                fallbackName: navData.driverExternalTrainingReport.idName,
                translatedName: translation.driverExternalTrainingReport,
                details: translation.driverExternalTrainingReport,
                reportPageEndpoint: navData.driverExternalTrainingReport.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.driverExternalTrainingReport.endpoint)
                  .commonReportPath,
                hidden: true,
              },
            ],
            hidden: true,
          },

          // Culture
          {
            ...navData.culture,
            name: navData.culture.idName,
            fallbackName: navData.culture.idName,
            translatedName: translation.culture,
            children: [
              {
                ...navData.privacy,
                name: navData.privacy.idName,
                fallbackName: navData.privacy.idName,
                reportPageEndpoint: navData.privacy.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.privacy.endpoint).commonReportPath,
                translatedName: translation.privacy,
                hidden: true,
              },
              {
                ...navData.altPrivacy,
                name: navData.altPrivacy.idName,
                fallbackName: navData.altPrivacy.idName,
                reportPageEndpoint: navData.altPrivacy.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.altPrivacy.endpoint).commonReportPath,
                translatedName: translation.altPrivacy,
                hidden: true,
              },
              {
                ...navData.policy,
                name: navData.policy.idName,
                fallbackName: navData.policy.idName,
                reportPageEndpoint: navData.policy.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.policy.endpoint).commonReportPath,
                translatedName: translation.policy,
                hidden: true,
              },
              {
                ...navData.pledge,
                name: navData.pledge.idName,
                fallbackName: navData.pledge.idName,
                reportPageEndpoint: navData.pledge.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.pledge.endpoint).commonReportPath,
                translatedName: translation.pledge,
                hidden: true,
              },
              {
                ...navData.managerPledge,
                name: navData.managerPledge.idName,
                fallbackName: 'Manager Policy - Pledge',
                reportPageEndpoint: navData.managerPledge.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.managerPledge.endpoint).commonReportPath,
                translatedName: translation.managerPledge,
                hidden: true,
              },
              {
                ...navData.mentorConsent,
                name: navData.mentorConsent.idName,
                fallbackName: 'Mentor - Privacy Consent',
                reportPageEndpoint: navData.mentorConsent.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.mentorConsent.endpoint).commonReportPath,
                translatedName: translation.mentorConsent,
                hidden: true,
              },
              {
                ...navData.authorizationToDrive,
                name: navData.authorizationToDrive.idName,
                fallbackName: navData.authorizationToDrive.idName,
                reportPageEndpoint: navData.authorizationToDrive.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.authorizationToDrive.endpoint)
                  .commonReportPath,
                translatedName: translation.authorizationToDrive,
                hidden: true,
              },
              {
                ...navData.travellingForWork,
                name: navData.travellingForWork.idName,
                fallbackName: navData.travellingForWork.idName,
                reportPageEndpoint: navData.travellingForWork.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.travellingForWork.endpoint).commonReportPath,
                translatedName: translation.travellingForWork,
                hidden: true,
              },
              {
                ...navData.tutorialVideos,
                name: navData.tutorialVideos.idName,
                fallbackName: navData.tutorialVideos.idName,
                reportPageEndpoint: navData.tutorialVideos.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.tutorialVideos.endpoint).commonReportPath,
                translatedName: translation.tutorialVideos,
                hidden: true,
              },
              {
                ...navData.healthQuestionnaire,
                name: navData.healthQuestionnaire.idName,
                fallbackName: navData.healthQuestionnaire.idName,
                reportPageEndpoint: navData.healthQuestionnaire.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.healthQuestionnaire.endpoint)
                  .commonReportPath,
                translatedName: translation.healthQuestionnaire,
                hidden: true,
              },
              {
                ...navData.riskFoundation,
                name: navData.riskFoundation.idName,
                customName: this.getCustomName(navData.riskFoundation.idName),
                fallbackName: navData.riskFoundation.idName,
                reportPageEndpoint: navData.riskFoundation.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.riskFoundation.endpoint).commonReportPath,
                translatedName: translation.riskFoundation,
                hidden: true,
              },
              {
                ...navData.alternativeRiskFoundation,
                name: navData.alternativeRiskFoundation.idName,
                customName: this.getCustomName(navData.alternativeRiskFoundation.idName),
                fallbackName: navData.alternativeRiskFoundation.idName,
                reportPageEndpoint: navData.alternativeRiskFoundation.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.alternativeRiskFoundation.endpoint)
                  .commonReportPath,
                translatedName: translation.altRiskFoundation,
                hidden: true,
              },
              {
                ...navData.driverDetails,
                name: navData.driverDetails.idName,
                fallbackName: navData.driverDetails.idName,
                reportPageEndpoint: navData.driverDetails.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.driverDetails.endpoint).commonReportPath,
                translatedName: translation.driverDetails,
                hidden: true,
              },
            ],
            hidden: true,
          },

          // RoadRISK
          {
            ...navData.roadRisk,
            name: navData.roadRisk.idName,
            fallbackName: navData.roadRisk.idName,
            translatedName: translation.roadRisk,
            trademark: 'R',
            children: [
              {
                ...navData.profileSummary,
                name: navData.profileSummary.idName,
                fallbackName: navData.profileSummary.idName,
                reportPageEndpoint: navData.profileSummary.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.profileSummary.endpoint).commonReportPath,
                translatedName: translation.profile + ' ' + translation.summary,
                hidden: true,
              },
              {
                ...navData.profileInsights,
                name: navData.profileInsights.idName,
                fallbackName: navData.profileInsights.idName,
                url: '#/profile-insights',
                translatedName: translation.profileInsights,
                hidden: true,
              },
              {
                ...navData.defensiveDriving,
                name: navData.defensiveDriving.idName,
                fallbackName: navData.defensiveDriving.idName,
                reportPageEndpoint: navData.defensiveDriving.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.defensiveDriving.endpoint).commonReportPath,
                translatedName: translation.defensiveDriving,
                hidden: true,
              },
              {
                ...navData.feedback,
                name: navData.feedback.idName,
                fallbackName: navData.feedback.idName,
                reportPageEndpoint: navData.feedback.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.feedback.endpoint).commonReportPath,
                translatedName: translation.feedback,
                hidden: true,
              },
              {
                ...navData.driverRiskRating,
                name: navData.driverRiskRating.idName,
                fallbackName: navData.driverRiskRating.idName,
                reportPageEndpoint: navData.driverRiskRating.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.driverRiskRating.endpoint).commonReportPath,
                translatedName: translation.driverRiskRating,
                hidden: true,
              },
              {
                ...navData.riskExposureSummary,
                name: navData.riskExposureSummary.idName,
                fallbackName: navData.riskExposureSummary.idName,
                reportPageEndpoint: navData.riskExposureSummary.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.riskExposureSummary.endpoint)
                  .commonReportPath,
                translatedName: translation.riskExposureSummary,
                hidden: true,
              },
            ],
            hidden: true,
          },

          // RoadRISK PIV
          {
            ...navData.roadRiskPIV,
            name: navData.roadRiskPIV.idName,
            fallbackName: navData.roadRisk.idName,
            translatedName: translation.roadRisk,
            trademark: 'R',
            fallbackTextAfterTrademark: 'PIV',
            translatedTextAfterTrademark: translation.PIV,
            children: [
              {
                ...navData.profileSummaryRoadRiskPIV,
                name: navData.profileSummaryRoadRiskPIV.idName,
                fallbackName: navData.profileSummaryRoadRiskPIV.idName,
                reportPageEndpoint: navData.profileSummaryRoadRiskPIV.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.profileSummaryRoadRiskPIV.endpoint)
                  .commonReportPath,
                translatedName: translation.profile + ' ' + translation.summary,
                hidden: true,
              },
              {
                ...navData.defensiveOperating,
                name: navData.defensiveOperating.idName,
                fallbackName: navData.defensiveOperating.idName,
                reportPageEndpoint: navData.defensiveOperating.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.defensiveOperating.endpoint)
                  .commonReportPath,
                translatedName: translation.defensiveOperating,
                hidden: true,
              },
              {
                ...navData.operatorRiskRating,
                name: navData.operatorRiskRating.idName,
                fallbackName: navData.operatorRiskRating.idName,
                reportPageEndpoint: navData.operatorRiskRating.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.operatorRiskRating.endpoint)
                  .commonReportPath,
                translatedName: translation.operatorRiskRating,
                hidden: true,
              },
            ],
            hidden: true,
          },

          // eLearning
          {
            ...navData.eLearning,
            name: navData.eLearning.idName,
            fallbackName: navData.eLearning.idName,
            translatedName: translation.eLearning,
            children: [
              {
                ...navData.oneMoreSecond,
                name: navData.oneMoreSecond.idName,
                fallbackName: navData.oneMoreSecond.idName,
                translatedName: translation.oneMoreSecond,
                reportPageEndpoint: navData.oneMoreSecond.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.oneMoreSecond.endpoint).commonReportPath,
                hidden: true,
              },
              {
                ...navData.riskCoachReport,
                name: navData.riskCoachReport.idName,
                fallbackName: navData.riskCoachReport.idName,
                translatedName: translation.riskCoach,
                trademark: 'R',
                reportPageEndpoint: navData.riskCoachReport.endpoint,
                hasEndpointFilters: true,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.riskCoachReport.endpoint).commonReportPath,
                hidden: true,
              },
            ],
            hidden: true,
          },

          // DriverINDEX
          {
            ...navData.driverIndex,
            name: navData.driverIndex.idName,
            fallbackName: navData.driverIndex.idName,
            translatedName: translation.driverIndex,
            trademark: 'R',
            children: [
              {
                ...navData.scorecard,
                name: navData.scorecard.idName,
                fallbackName: navData.scorecard.idName,
                details: translation.driverIndex,
                reportPageEndpoint: navData.scorecard.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.scorecard.endpoint).commonReportPath,
                translatedName: translation.scorecard,
              },
              {
                ...navData.mentorByEdriving,
                name: navData.mentorByEdriving.idName,
                fallbackName: navData.mentorByEdriving.idName,
                details: translation.driverIndex,
                reportPageEndpoint: navData.mentorByEdriving.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.mentorByEdriving.endpoint).commonReportPath,
                translatedName: translation.mentorByEdriving,
                hidden: true,
              },

              {
                ...navData.incidents,
                name: navData.incidents.idName,
                fallbackName: navData.incidents.idName,
                details: translation.driverIndex,
                reportPageEndpoint: navData.incidents.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.incidents.endpoint).commonReportPath,
                translatedName: translation.incidents,
                hidden: true,
              },

              {
                ...navData.eventsTable,
                name: navData.eventsTable.idName,
                fallbackName: navData.eventsTable.idName,
                details: translation.driverIndex,
                reportPageEndpoint: navData.eventsTable.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.eventsTable.endpoint).commonReportPath,
                translatedName: translation.eventsTable,
                hidden: true,
              },

              {
                ...navData.riskLevelChanges,
                name: navData.riskLevelChanges.idName,
                fallbackName: navData.riskLevelChanges.idName,
                details: translation.driverIndex,
                reportPageEndpoint: navData.riskLevelChanges.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.riskLevelChanges.endpoint).commonReportPath,
                translatedName: translation.riskLevelChanges,
                hidden: true,
              },

              {
                ...navData.universe,
                name: navData.universe.idName,
                fallbackName: navData.universe.idName,
                details: translation.driverIndex,
                reportPageEndpoint: navData.universe.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.universe.endpoint).commonReportPath,
                translatedName: translation.universe,
                hidden: true,
              },

              {
                ...navData.incidentsCollisions,
                name: navData.incidentsCollisions.idName,
                fallbackName: navData.incidentsCollisions.idName,
                details: translation.driverIndex,
                reportPageEndpoint: navData.incidentsCollisions.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.incidentsCollisions.endpoint)
                  .commonReportPath,
                translatedName: translation.incidentsCollisions,
                hidden: true,
              },
              {
                ...navData.compare,
                name: navData.compare.idName,
                fallbackName: navData.compare.idName,
                details: translation.driverIndex,
                reportPageEndpoint: navData.compare.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.compare.endpoint).commonReportPath,
                translatedName: translation.compare,
                hidden: true,
              },
              {
                ...navData.distanceTravelled,
                name: navData.distanceTravelled.idName,
                fallbackName: navData.distanceTravelled.idName,
                translatedName: translation.distanceTravelled,
                details: translation.driverIndex,
                reportPageEndpoint: navData.distanceTravelled.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.distanceTravelled.endpoint).commonReportPath,
                hidden: true,
              },
              {
                ...navData.workHours,
                name: navData.workHours.idName,
                fallbackName: navData.workHours.idName,
                translatedName: translation.workHours,
                details: translation.driverIndex,
                reportPageEndpoint: navData.workHours.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.workHours.endpoint).commonReportPath,
                hidden: true,
              },
              {
                ...navData.mentorRewards,
                name: navData.mentorRewards.idName,
                fallbackName: navData.mentorRewards.idName,
                translatedName: translation.mentorRewards,
                details: translation.driverIndex,
                reportPageEndpoint: navData.mentorRewards.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.mentorRewards.endpoint).commonReportPath,
                hidden: true,
              },
            ],
            hidden: true,
          },

          // Coaching
          {
            ...navData.coaching,
            name: navData.coaching.idName,
            fallbackName: navData.coaching.idName,
            translatedName: translation.coaching,
            children: [
              {
                ...navData.coachingCompletions,
                name: navData.coachingCompletions.idName,
                fallbackName: navData.coachingCompletions.idName,
                translatedName: translation.coachingCompletions,
                reportPageEndpoint: navData.coachingCompletions.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.coachingCompletions.endpoint)
                  .commonReportPath,
                hidden: true,
              },
              {
                ...navData.amazonCoachingAssignments,
                name: navData.amazonCoachingAssignments.idName,
                fallbackName: navData.amazonCoachingAssignments.idName,
                translatedName: translation.amazonCoachingAssignments,
                reportPageEndpoint: navData.amazonCoachingAssignments.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.amazonCoachingAssignments.endpoint)
                  .commonReportPath,
                hidden: true,
                notificationsBadge: {
                  display: true,
                  tooltip: 'MAINSIDENAV.amazonCountBadgeTooltip',
                },
              },
            ],
            hidden: true,
          },

          // Coaching Recommendations
          {
            ...navData.coachingRecommendations,
            name: navData.coachingRecommendations.idName,
            fallbackName: navData.coachingRecommendations.idName,
            translatedName: translation.coachingRecommendations,
            hidden: true,
            children: [
              {
                ...navData.DriverImprovementReview,
                name: navData.DriverImprovementReview.idName,
                fallbackName: navData.DriverImprovementReview.idName,
                reportPageEndpoint: navData.DriverImprovementReview.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.DriverImprovementReview.endpoint)
                  .commonReportPath,
                translatedName: translation.DriverImprovementReview,
                hidden: true,
                notificationsBadge: {
                  display: true,
                  tooltip: 'COACHING_RECOMMENDATIONS.totalNumberOfRecommendations',
                },
              },
              {
                ...navData.DriverIndexProCoach,
                name: navData.DriverIndexProCoach.idName,
                fallbackName: navData.DriverIndexProCoach.idName,
                reportPageEndpoint: navData.DriverIndexProCoach.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.DriverIndexProCoach.endpoint)
                  .commonReportPath,
                translatedName: translation.DriverIndexProCoach,
                hidden: true,
                notificationsBadge: {
                  display: true,
                  tooltip: 'COACHING_RECOMMENDATIONS.totalNumberOfRecommendations',
                },
              },
              {
                ...navData.PccReview,
                name: navData.PccReview.idName,
                fallbackName: navData.PccReview.idName,
                reportPageEndpoint: navData.PccReview.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.PccReview.endpoint).commonReportPath,
                translatedName: translation.PccReview,
                hidden: true,
                notificationsBadge: {
                  display: true,
                  tooltip: 'COACHING_RECOMMENDATIONS.totalNumberOfRecommendations',
                },
              },
              {
                ...navData.PccReviewProCoach,
                name: navData.PccReviewProCoach.idName,
                fallbackName: navData.PccReviewProCoach.idName,
                reportPageEndpoint: navData.PccReviewProCoach.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.PccReviewProCoach.endpoint).commonReportPath,
                translatedName: translation.PccReviewProCoach,
                hidden: true,
                notificationsBadge: {
                  display: true,
                  tooltip: 'COACHING_RECOMMENDATIONS.totalNumberOfRecommendations',
                },
              },
              {
                ...navData.OneToOne,
                name: navData.OneToOne.idName,
                fallbackName: navData.OneToOne.idName,
                reportPageEndpoint: navData.OneToOne.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.OneToOne.endpoint).commonReportPath,
                translatedName: translation.OneToOne,
                hidden: true,
                notificationsBadge: {
                  display: true,
                  tooltip: 'COACHING_RECOMMENDATIONS.totalNumberOfRecommendations',
                },
              },
              {
                ...navData.OneToOneProCoach,
                name: navData.OneToOneProCoach.idName,
                fallbackName: navData.OneToOneProCoach.idName,
                reportPageEndpoint: navData.OneToOneProCoach.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.OneToOneProCoach.endpoint).commonReportPath,
                translatedName: translation.OneToOneProCoach,
                hidden: true,
                notificationsBadge: {
                  display: true,
                  tooltip: 'COACHING_RECOMMENDATIONS.totalNumberOfRecommendations',
                },
              },
              {
                ...navData.OneWeekFICO_LT500,
                name: navData.OneWeekFICO_LT500.idName,
                fallbackName: navData.OneWeekFICO_LT500.idName,
                reportPageEndpoint: navData.OneWeekFICO_LT500.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.OneWeekFICO_LT500.endpoint).commonReportPath,
                translatedName: translation.OneWeekFico_LT500,
                hidden: true,
                notificationsBadge: {
                  display: true,
                  tooltip: 'COACHING_RECOMMENDATIONS.totalNumberOfRecommendations',
                },
              },
              {
                ...navData.TwoWeeksFICO_LT710,
                name: navData.TwoWeeksFICO_LT710.idName,
                fallbackName: navData.TwoWeeksFICO_LT710.idName,
                reportPageEndpoint: navData.TwoWeeksFICO_LT710.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.TwoWeeksFICO_LT710.endpoint)
                  .commonReportPath,
                translatedName: translation.TwoWeeksFICO_LT710,
                hidden: true,
                notificationsBadge: {
                  display: true,
                  tooltip: 'COACHING_RECOMMENDATIONS.totalNumberOfRecommendations',
                },
              },
            ],
          },

          // Licence Checking
          {
            ...navData.licenceChecking,
            name: navData.licenceChecking.idName,
            fallbackName: navData.licenceChecking.idName,
            translatedName: translation.licenceChecking,
            hidden: true,
            children: [
              {
                ...navData.licenceAdmin,
                name: navData.licenceAdmin.idName,
                fallbackName: navData.licenceAdmin.idName,
                reportPageEndpoint: navData.licenceAdmin.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.licenceAdmin.endpoint).commonReportPath,
                translatedName: translation.licenceAdmin,
              },
              {
                ...navData.licenceCheckingResults,
                name: navData.licenceCheckingResults.idName,
                fallbackName: navData.licenceCheckingResults.idName,
                reportPageEndpoint: navData.licenceCheckingResults.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.licenceCheckingResults.endpoint)
                  .commonReportPath,
                translatedName: translation.licenceCheckingResults,
              },
              {
                ...navData.nonUkLicenceHolders,
                name: navData.nonUkLicenceHolders.idName,
                fallbackName: navData.nonUkLicenceHolders.idName,
                reportPageEndpoint: navData.nonUkLicenceHolders.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.nonUkLicenceHolders.endpoint)
                  .commonReportPath,
                translatedName: translation.nonUkLicenceHolders,
              },
              {
                ...navData.licenceTachograph,
                name: navData.licenceTachograph.idName,
                fallbackName: navData.licenceTachograph.idName,
                reportPageEndpoint: navData.licenceTachograph.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.licenceTachograph.endpoint).commonReportPath,
                translatedName: translation.licenceTachograph,
              },
              {
                ...navData.licenceCpc,
                name: navData.licenceCpc.idName,
                fallbackName: navData.licenceCpc.idName,
                reportPageEndpoint: navData.licenceCpc.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.licenceCpc.endpoint).commonReportPath,
                translatedName: translation.licenceCpc,
              },
            ],
          },

          // MVR
          {
            ...navData.mvrPlus,
            name: navData.mvrPlus.idName,
            fallbackName: navData.mvrPlus.idName,
            translatedName: translation.mvrPlus,
            children: [
              {
                ...navData.main,
                name: navData.main.idName,
                fallbackName: navData.main.idName,
                reportPageEndpoint: navData.main.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.main.endpoint).commonReportPath,
                translatedName: translation.main,
                hidden: true,
              },
              {
                ...navData.drm,
                name: navData.drm.idName,
                fallbackName: navData.drm.idName,
                reportPageEndpoint: navData.drm.endpoint,
                details: translation.driverRiskMonitoring,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.drm.endpoint).commonReportPath,
                translatedName: translation.drm,
                hidden: true,
              },
              {
                ...navData.administration,
                name: navData.administration.idName,
                fallbackName: navData.administration.idName,
                reportPageEndpoint: navData.administration.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.administration.endpoint).commonReportPath,
                translatedName: translation.administration,
                hidden: true,
              },
            ],
            hidden: true,
          },

          // Login Logout Report
          {
            ...navData.loginLogout,
            name: navData.loginLogout.idName,
            fallbackName: navData.loginLogout.idName,
            url: '#/mentor-login-report',
            translatedName: translation.loginLogout,
            hidden: true,
            notificationsBadge: {
              display: true,
              tooltip: 'MESSAGESANDWARNINGS.totalDriversToday',
            },
          },

          // Vehicle Report
          {
            ...navData.vehicleReports,
            name: navData.vehicleReports.idName,
            fallbackName: navData.vehicleReports.idName,
            translatedName: translation.vehicleReports,

            children: [
              {
                ...navData.dvir,
                name: navData.dvir.idName,
                fallbackName: navData.dvir.idName,
                reportPageEndpoint: navData.dvir.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.dvir.endpoint).commonReportPath,
                translatedName: translation.fleetReportDvir,
                hidden: true,
                notificationsBadge: {
                  display: true,
                  tooltip: 'DVCR.totalDamagesToday',
                },
              },
              {
                ...navData.damageReport,
                name: navData.damageReport.idName,
                fallbackName: navData.damageReport.idName,
                reportPageEndpoint: navData.damageReport.endpoint,
                url: `#/fleet-damages-report`,
                translatedName: translation.damagesReport,
                hidden: true,
              },
            ],
            hidden: true,
          },

          // DVCR
          {
            ...navData.dvcr,
            name: navData.dvcr.idName,
            fallbackName: navData.dvcr.idName,
            url: `#/fleet-report-dvcr`,
            translatedName: translation.fleetReportDvcr,
            hidden: true,
            notificationsBadge: {
              display: true,
              tooltip: 'DVCR.totalDamagesToday',
            },
          },

          // Top Level Damages Report
          {
            ...navData.damageReport,
            name: navData.damageReport.idName,
            fallbackName: navData.damageReport.idName,
            reportPageEndpoint: navData.damageReport.endpoint,
            url: `#/damages-report`,
            translatedName: translation.damagesReport,
            hidden: true,
          },

          // Manager Tools
          {
            ...navData.managerTools,
            name: navData.managerTools.idName,
            fallbackName: navData.managerTools.idName,
            translatedName: translation.managerTools,
            children: [
              {
                ...navData.managerActivityReport,
                name: navData.managerActivityReport.idName,
                fallbackName: navData.managerActivityReport.idName,
                translatedName: translation.managerActivityReport,
                reportPageEndpoint: navData.managerActivityReport.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.managerActivityReport.endpoint)
                  .commonReportPath,
                hidden: true,
              },
              {
                ...navData.misUsers,
                name: navData.misUsers.idName,
                fallbackName: navData.misUsers.idName,
                translatedName: translation.misUsers,
                reportPageEndpoint: navData.misUsers.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.misUsers.endpoint).commonReportPath,
                hidden: true,
              },
              {
                ...navData.driverList,
                name: navData.driverList.idName,
                fallbackName: navData.driverList.idName,
                translatedName: translation.driverList,
                reportPageEndpoint: navData.driverList.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.driverList.endpoint).commonReportPath,
                hidden: true,
              },
              {
                ...navData.customInformationContent,
                name: navData.customInformationContent.idName,
                fallbackName: navData.customInformationContent.idName,
                translatedName: translation.customInformationContent,
                reportPageEndpoint: navData.customInformationContent.endpoint,
                url: this.getConciseEndpointAndCommonReportNavPath(navData.customInformationContent.endpoint)
                  .commonReportPath,
                hidden: true,
              },
            ],
            hidden: true,
          },

          // Trip Session Report
          {
            ...navData.tripSessionReport,
            name: navData.tripSessionReport.idName,
            fallbackName: navData.tripSessionReport.idName,
            url: `#/trip-session-report`,
            translatedName: translation.tripSessionReport,
            expanded: false,
            selected: false,
            hidden: true,
          },

          {
            ...navData.shiftComplianceReport,
            name: navData.shiftComplianceReport.idName,
            fallbackName: navData.shiftComplianceReport.idName,
            url: this.getConciseEndpointAndCommonReportNavPath(navData.shiftComplianceReport.endpoint).commonReportPath,
            translatedName: translation.shiftComplianceReport,
            expanded: false,
            selected: false,
            hidden: true,
          },

          // Driver Event Reporting
          {
            ...navData.der,
            name: navData.der.idName,
            fallbackName: navData.der.idName,
            translatedName: translation.der,
            reportPageEndpoint: navData.der.endpoint,
            url: this.getConciseEndpointAndCommonReportNavPath(navData.der.endpoint).commonReportPath,
            hidden: true,
            notificationsBadge: {
              display: true,
              tooltip: 'MAINSIDENAV.derBadgeTooltip',
            },
          },

          // Self Provisioning
          {
            ...navData.users,
            name: navData.users.idName,
            fallbackName: navData.users.idName,
            url: `#/self-provisioning`,
            translatedName: translation.selfProvisioning,
            expanded: false,
            selected: false,
            hidden: true,
          },

          // MIS Notifications
          {
            ...navData.misNotifications,
            name: navData.misNotifications.idName,
            fallbackName: navData.misNotifications.idName,
            translatedName: translation.misNotifications,
            reportPageEndpoint: navData.misNotifications.endpoint,
            url: this.getConciseEndpointAndCommonReportNavPath(navData.misNotifications.endpoint).commonReportPath,
            expanded: false,
            selected: false,
            hidden: true,
            notificationsBadge: {
              display: true,
              tooltip: 'CUSTOM_CONTENT.totalActiveNotifications',
            },
          },

          // GUIDES
          {
            ...navData.guidesFaq,
            name: navData.guidesFaq.idName,
            fallbackName: navData.guidesFaq.idName,
            translatedName: translation.guides + ' / ' + translation.faq?.toUpperCase(),
            children: [
              {
                ...navData.managerGuides,
                name: navData.managerGuides.idName,
                fallbackName: navData.managerGuides.idName,
                translatedName: translation.company,
                url: '#/company-guide',
                hidden: false,
              },
              {
                ...navData.vrmGuides,
                name: navData.vrmGuides.idName,
                fallbackName: navData.vrmGuides.idName,
                translatedName: translation.vrm,
                url: '#/vrm-guide',
                hidden: false,
              },
              {
                ...navData.mentorFaqExternalLink,
                name: navData.mentorFaqExternalLink.idName,
                fallbackName: navData.mentorFaqExternalLink.idName,
                translatedName: translation.mentorFaqExternalLink,
                url: this.userProfile ? this.userProfile.mentorFaqUrl : '',
                isExternalUrl: true,
                openInNewTab: true,
                hidden: true,
              },
            ],
            hidden: true,
          },

          // eDriving 360 Magazine
          {
            ...navData.threeSixty,
            name: navData.threeSixty.idName,
            fallbackName: navData.threeSixty.idName,
            translatedName: translation.three60Magazine,
            url: `${this.appSettings.eDrivingWebsite}/three60`,
            isExternalUrl: true,
            openInNewTab: true,
            hidden: true,
          },
          // Driver Import
          {
            ...navData.driverImport,
            name: navData.driverImport.idName,
            fallbackName: navData.driverImport.idName,
            translatedName: translation.driverImport,
            url: `#/driver-import`,
            expanded: false,
            selected: false,
            hidden: true,
          },
          // Contact Us - Amazon & Others
          {
            ...navData.contactUs,
            name: navData.contactUs.idName,
            fallbackName: navData.contactUs.idName,
            translatedName: translation.contactUs,
            url:
              this.userProfile && this.userProfile.contactUsUrl
                ? this.userProfile.contactUsUrl
                : `${this.appSettings.eDrivingWebsite}/support/`,
            isExternalUrl: true,
            openInNewTab: true,
            hidden: true,
          },
        ];

        this.setNavigationPermissions();

        console.log('NAVIGATION', this.navigation);
        this.identifySelectedNavAndParent();
      },
    });
  }

  public setAndCacheLanguageCode(languageCode: string, reload: boolean = true): void {
    if (languageCode) {
      this.languageParam = languageCode.toLowerCase();
      this.setSelectedLanguage(this.languageParam);

      if (this.userProfile) {
        this.userProfile.languageCode = this.languageParam;
        this.setUserProfileToLocalStorage();
      }
    } else {
      // Check for language in Local Storage
      const storedLang = localStorage.getItem(this.localStorageKeys.language);

      if (storedLang) {
        this.setSelectedLanguage(storedLang);
        this.languageParam = storedLang;
      } else {
        this.setSelectedLanguage('en');
        this.languageParam = 'en';

        if (this.userProfile) {
          this.userProfile.languageCode = 'en';
          this.setUserProfileToLocalStorage();
        }
      }
    }

    this.resetOneTrustConsent(() => {
      if (reload) {
        this.reloadingLanguage = true;

        const reloading = this.readyTranslations.generic.reloading
          ? this.readyTranslations.generic.reloading
          : 'Reloading';

        this.showToast(reloading + '...');
        this.reloadAndTryToApplyUpdates(true);
      } else {
        this.processLanguageTranslations(this.selectedLanguage);
      }
    });
  }

  private resetOneTrustConsent(callback?: () => void) {
    const oneTrustKeys = ['OptanonConsent', 'OptanonAlertBoxClosed'];
    this.deleteCookies(oneTrustKeys);

    if (callback) {
      setTimeout(() => {
        callback();
      }, 500);
    }
  }

  private deleteCookies(cookieKeys: Array<string>) {
    const currentCookies = document.cookie;

    if (currentCookies && cookieKeys && cookieKeys.length) {
      cookieKeys.forEach((cookieKey: string) => {
        if (currentCookies.includes(cookieKey)) {
          document.cookie = `${cookieKey}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        }
      });
    }
  }

  public setSelectedLanguage(language: string): void {
    this.selectedLanguage = this.getLanguageByCode(language);

    if (this.selectedLanguage && this.selectedLanguage.code) {
      localStorage.setItem(this.localStorageKeys.language, this.selectedLanguage.code.toLowerCase());
    } else {
      this.selectedLanguage = this.defaultLanguage;
    }

    this._languageSet.next(this.selectedLanguage);
  }

  /*
	NOTE: This would only set cService's version of userProfile
  */
  public setUserProfileToLocalStorage() {
    localStorage.setItem(this.localStorageKeys.userProfile, JSON.stringify(this.userProfile));
  }

  public identifySelectedNavAndParent(): void {
    setTimeout(() => {
      this.navigation.forEach((navigation: Navigation) => {
        const currentPage = this.predictPageNameFromUrl();

        if (currentPage && currentPage.page) {
          // All OFF Initially
          navigation.selected = false;
          navigation.expanded = false;

          if (navigation.children && navigation.children.length > 0) {
            navigation.children.forEach((child: NavigationChild) => {
              child.selected = false;
            });

            let hasSelectedChild;

            if (currentPage.isCommonReport) {
              hasSelectedChild = navigation.children.find((child: NavigationChild) => {
                let selected = false;
                // Process Common Report
                if (child.reportPageEndpoint && child.url) {
                  const childLocation = this.predictPageNameFromUrl(child.url).page;

                  if (childLocation) {
                    selected = childLocation.toLowerCase() === currentPage.page.toLowerCase();
                    if (selected === true) {
                      child.selected = true;
                      this.getNavigatedReportData(child, true);
                    }
                  }
                }

                return selected;
              });
            } else if (currentPage.isVrmAnalyticsDashboard) {
              hasSelectedChild = navigation.children.find((child: NavigationChild) => {
                let selected = false;

                // Process Dashboard
                if (child.isVrmAnalyticsDashboard && child.dashboardGroup && child.url) {
                  const childLocation = this.predictPageNameFromUrl(child.url).page;

                  if (childLocation) {
                    selected = childLocation.toLowerCase() === currentPage.page.toLowerCase();
                    if (selected === true) {
                      child.selected = true;
                      this.getNavigatedReportData(child, true);
                    }
                  }
                }

                return selected;
              });
            } else {
              hasSelectedChild = navigation.children.find((child: NavigationChild) => {
                let selected = false;

                if (child.url) {
                  const childLocation = this.predictPageNameFromUrl(child.url).page;

                  if (childLocation) {
                    selected = childLocation.toLowerCase() === currentPage.page.toLowerCase();
                    if (selected === true) {
                      child.selected = true;
                      this.getNavigatedReportData(child, false);
                    }
                  }
                }

                return selected;
              });
            }

            if (hasSelectedChild) {
              navigation.selected = true;
              navigation.expanded = true;
            }
          }

          // Process selection for nav without children
          if (navigation.url) {
            const navLocation = this.predictPageNameFromUrl(navigation.url);

            if (navLocation.isCommonReport) {
              const commonReportLocation = navLocation.page;

              if (commonReportLocation.toLowerCase() === currentPage.page.toLowerCase()) {
                navigation.selected = true;
                this.getNavigatedReportData(navigation, true);
              }
            } else {
              if (navLocation.page === currentPage.page.toLowerCase()) {
                navigation.selected = true;
                this.getNavigatedReportData(navigation, false);
              }
            }
          }
        }
      });

      const highLevelNav = this.navigation.find((nav) => nav.selected === true);

      if (highLevelNav) {
        if (highLevelNav.children && highLevelNav.children.length > 0) {
          const childNavTitle = highLevelNav.children.find((child) => child.selected === true);

          this.commonReportTitle = childNavTitle.translatedName
            ? childNavTitle.translatedName
            : childNavTitle.fallbackName;
        } else {
          this.commonReportTitle = highLevelNav.translatedName
            ? highLevelNav.translatedName
            : highLevelNav.fallbackName;
        }
      }
    }, 100);
  }

  /**
   * Use especially when nav identification is likely to
   * be wrongly predicted due to manual URL manipulation
   * by the user.
   */
  clearNavSelection() {
    this.navigation.map((nav) => {
      if (nav.selected) {
        nav.selected = false;
      }

      if (nav.children) {
        nav.children.map((child) => {
          if (child.selected) {
            child.selected = false;
          }
        });
      }
    });
  }

  reloadAndTryToApplyUpdates(forceReload = false) {
    if (forceReload) {
      location.reload();
    }

    if (this.appUpdate.isEnabled) {
      this.appUpdate.checkForUpdate().then((updateAvailable) => {
        if (updateAvailable) {
          location.reload();
        }
      });
    } else {
      location.reload();
    }
  }

  public tryApplyingUpdatesIfAvailable() {
    if (this.appUpdate.isEnabled) {
      this.appUpdate.checkForUpdate().then((updateAvailable) => {
        if (updateAvailable) {
          this.appUpdate.activateUpdate().then(() => location.reload());
        }
      });
    }
  }

  public predictPageNameFromUrl(url?: string): PageName {
    const pageName: PageName = {
      page: '',
      isCommonReport: false,
      isDashboardGroup: false,
      isVrmAnalyticsDashboard: false,
    };

    const currentLocation = url ? url : this.location.path();
    let locationStrings = currentLocation.split('?')[0].split('/');
    locationStrings = locationStrings.filter((loc) => loc != '' && loc != `#`);

    if (currentLocation.toLowerCase().includes(this.commonReports)) {
      pageName.page = locationStrings[1];
      pageName.isCommonReport = true;
    } else if (currentLocation.toLowerCase().includes(this.vrmAnalyticsDashboards)) {
      pageName.page = locationStrings[1] || locationStrings[0];
      pageName.isVrmAnalyticsDashboard = true;
    } else if (currentLocation.toLowerCase().includes(this.dashboards)) {
      pageName.page = locationStrings[1] || locationStrings[0];
      pageName.isDashboardGroup = true;
    } else {
      pageName.page = locationStrings[0];
    }

    return pageName;
  }

  private getNavigatedReportData(currentNav: Navigation | NavigationChild, isCommonReport: boolean) {
    this.navigatedReport = {
      commonReportPath: isCommonReport ? currentNav.url : '',
      endpoint: currentNav.reportPageEndpoint,
      navigationData: currentNav,
    };

    let pageTitle = this.getNavTitle(currentNav);
    if (pageTitle && pageTitle.toLowerCase().includes('edriving')) {
      // Apply trademark
      pageTitle = pageTitle.replace(/eDriving/g, 'eDriving℠');
    }

    setTimeout(() => {
      this.pageHeader.pageTitle = pageTitle;
    });
  }

  public navigateToPage(page: string) {
    const currentPage = this.predictPageNameFromUrl(page);
    if (currentPage.isCommonReport) {
      this.commonReportTitle = this.toTitleCase(currentPage.page);
    }

    this.router.navigate([page]);
    this.identifySelectedNavAndParent();
  }

  public setNavigationPermissions() {
    if (this.userProfile) {
      const userPermissions = this.userProfile.userPermissions;
      const companyPermissions = this.userProfile.companyPermissions;
      const distributorPermissions = this.userProfile.distributorPermissions;

      // The Risk Foundations are loaded dynamically from the back-end upon login so are set here
      if (this.userProfile.riskFoundationCourses) {
        culture.riskFoundation.codes = this.userProfile.riskFoundationCourses;
      }

      if (this.userProfile.userPermissions && this.userProfile.companyPermissions) {
        this.navigation.forEach((navigation: Navigation) => {
          const children = navigation.children;

          // AMZL related Amazon companies
          if (navigation.name.toLowerCase() === navData.dspPerformance.idName.toLowerCase()) {
            if (
              this.userProfile.distributorPermissions &&
              distributorPermissions.permMisMenuDspPerformance === this.permLevels.TRUE
            ) {
              navigation.hidden = false;
            }
          } else if (navigation.name.toLowerCase() == navData.vehicleReports.idName.toLowerCase()) {
            children.forEach((child: NavigationChild) => {
              if (child.name.toLowerCase() === navData.dvir.idName.toLowerCase()) {
                if (companyPermissions.permMisMenuDvir == this.permLevels.TRUE) {
                  child.hidden = false;
                }
              } else if (child.name.toLowerCase() === navData.damageReport.idName.toLowerCase()) {
                if (
                  companyPermissions.permMisMenuDamagesReport == this.permLevels.TRUE &&
                  companyPermissions.permMisMenuDvir == this.permLevels.TRUE
                ) {
                  child.hidden = false;
                }
              }
            });

            const hasChildNav = children.find((childNav) => childNav.hidden === false);
            if (hasChildNav) {
              navigation.hidden = false;
            }
          } else if (navigation.name.toLowerCase() == navData.dvcr.idName.toLowerCase()) {
            if (this.userProfile.distributorPermissions) {
              if (distributorPermissions.permMisMenuDvcr === this.permLevels.TRUE) {
                navigation.hidden = false;
              }
            } else {
              if (companyPermissions.permMisMenuDvcr == this.permLevels.TRUE) {
                navigation.hidden = false;
              }
            }
          } else if (
            navigation.name.toLowerCase() == navData.loginLogout.idName.toLowerCase() &&
            userPermissions.permLoginLogout === this.permLevels.TRUE
          ) {
            navigation.hidden = !this.userProfile.fpId;
          } else if (navigation.name.toLowerCase() == navData.damageReport.idName.toLowerCase()) {
            if (
              companyPermissions.permMisMenuDamagesReport == this.permLevels.TRUE &&
              companyPermissions.permMisMenuDvir == this.permLevels.FALSE
            ) {
              navigation.hidden = false;
            }
          } else if (navigation.name.toLowerCase() == navData.tripSessionReport.idName.toLowerCase()) {
            if (this.userProfile.distributorPermissions) {
              if (distributorPermissions.permMisMenuShiftReport === this.permLevels.TRUE) {
                navigation.hidden = false;
              }
            } else {
              if (companyPermissions.permMisMenuShiftReport === this.permLevels.TRUE) {
                navigation.hidden = false;
              }
            }
          } else if (
            navigation.name.toLowerCase() === navData.shiftComplianceReport.idName.toLowerCase() &&
            companyPermissions.permMisMenuShiftCompliance === this.permLevels.TRUE &&
            userPermissions.permMisMenuShiftCompliance === this.permLevels.TRUE
          ) {
            navigation.hidden = false;
          } else if (
            navigation.name.toLowerCase() == navData.der.idName.toLowerCase() &&
            [this.permLevels.view, this.permLevels.viewEdit].includes(userPermissions.permDriverEventReporting) &&
            companyPermissions.permMenuDriverEventReporting === this.permLevels.TRUE
          ) {
            navigation.hidden = false;
          } else if (
            navigation.name.toLowerCase() == navData.dspDer.idName.toLowerCase() &&
            this.userProfile.distributorPermissions &&
            distributorPermissions.permMisMenuDerReport === this.permLevels.TRUE
          ) {
            navigation.hidden = false;
          } else if (
            navigation.name.toLowerCase() == navData.users.idName.toLowerCase() &&
            companyPermissions.permMisMenuUsers === this.permLevels.TRUE
          ) {
            if (this.userProfile.isDistributor) {
              if (this.userProfile.isIdsUser) {
                navigation.hidden = false;
              }
            } else {
              navigation.hidden = userPermissions.permEditUsers === this.permLevels.none || !this.userProfile.fpId;
            }
          } else if (navigation.name.toLowerCase() == navData.culture.idName.toLowerCase()) {
            children.forEach((child: NavigationChild) => {
              if (child.name.toLowerCase() == navData.healthQuestionnaire.idName.toLowerCase()) {
                if (userPermissions.permHequ === this.permLevels.view) {
                  child.hidden = this.hasCourseCode(culture, child);
                }
              } else if (child.name.toLowerCase() == navData.alternativeRiskFoundation.idName.toLowerCase()) {
                child.hidden = this.hasCourseCodeInUse(culture, child);
              } else if (child.name.toLowerCase() === navData.privacy.idName.toLowerCase()) {
                if (companyPermissions.permMenuPrivacyNotice === this.permLevels.TRUE) {
                  child.hidden = false;
                }
              } else {
                child.hidden = this.hasCourseCode(culture, child);
              }
            });

            const hasChildNav = children.find((childNav) => childNav.hidden === false);
            if (hasChildNav) {
              navigation.hidden = false;
            }
          } else if (navigation.name.toLowerCase() == navData.eLearning.idName.toLowerCase()) {
            children.forEach((child: NavigationChild) => {
              if (child.name.toLowerCase() === navData.oneMoreSecond.idName.toLowerCase()) {
                if (companyPermissions.permMisMenuOms == this.permLevels.TRUE) {
                  child.hidden = false;
                }
              } else {
                // Same as Culture i.e. only shows when at least one eLearning course is found
                child.hidden = this.hasCourseCode(eLearning, child);

                if (this.userProfile.riskCoachCourses && this.userProfile.riskCoachCourses.length) {
                  child.hidden = false;
                }
              }
            });

            const hasChildNav = children.find((childNav) => childNav.hidden === false);
            if (hasChildNav) {
              navigation.hidden = false;
            }
          } else if (navigation.name.toLowerCase() == navData.driverActivityReporting.idName.toLowerCase()) {
            children.forEach((child: NavigationChild) => {
              if (
                child.name.toLowerCase() === navData.riskCoachDriverActivityDetail.idName.toLowerCase() &&
                companyPermissions.permMisMenuDriverActivityDetail === this.permLevels.TRUE
              ) {
                child.hidden = false;
              } else if (
                child.name.toLowerCase() == navData.driverActivitySummaryReport.idName.toLowerCase() &&
                companyPermissions.permMisDriverActivityReport === this.permLevels.TRUE
              ) {
                child.hidden = false;
              }
              if (
                child.name.toLowerCase() === navData.vrmCourseActivity.idName.toLowerCase() &&
                companyPermissions.permMisMenuVrmCourseActivityReport === this.permLevels.TRUE
              ) {
                child.hidden = false;
              }
              if (
                child.name.toLowerCase() == navData.driverActivityCompare.idName.toLowerCase() &&
                userPermissions.permDriverActivityCompare === this.permLevels.TRUE
              ) {
                child.hidden = false;
              }
            });

            const hasChildNav = children.find((childNav) => childNav.hidden === false);
            if (hasChildNav) {
              navigation.hidden = false;
            }
          } else if (navigation.name.toLowerCase() == navData.roadRisk.idName.toLowerCase()) {
            // Same as Culture i.e. only shows when at least one RR course is found
            children.forEach((child: NavigationChild) => {
              if (child.name === navData.profileInsights.idName) {
                if (
                  companyPermissions.permMisRoadRiskProfileInsights === this.permLevels.TRUE &&
                  userPermissions.permRoadRiskProfileInsights === this.permLevels.TRUE
                ) {
                  child.hidden = false;
                }
              } else if (child.name.toLowerCase() == navData.feedback.idName.toLowerCase()) {
                if (companyPermissions.permMenuRoadRiskFeedbackReport == this.permLevels.TRUE) {
                  child.hidden = false;
                }
              } else if (child.name.toLowerCase() == navData.riskExposureSummary.idName.toLowerCase()) {
                if (companyPermissions.permMenuRiskExposureSummary == this.permLevels.TRUE) {
                  child.hidden = this.hasCourseCode(roadRisk, child);
                }
              } else {
                child.hidden = this.hasCourseCode(roadRisk, child);
              }
            });

            const hasChildNav = children.find((childNav) => childNav.hidden === false);
            if (hasChildNav) {
              navigation.hidden = false;
            }
          } else if (navigation.name.toLowerCase() == navData.roadRiskPIV.idName.toLowerCase()) {
            children.forEach((child: NavigationChild) => {
              child.hidden = this.hasCourseCodeInUse(roadRiskFlt, child);
            });

            const hasChildNav = children.find((childNav) => childNav.hidden === false);
            if (hasChildNav) {
              navigation.hidden = false;
            }
          } else if (
            navigation.name.toLowerCase() == navData.driverExternalTraining.idName.toLowerCase() &&
            companyPermissions.permMisExternalTraining === this.permLevels.TRUE
          ) {
            navigation.hidden = false;
            children.forEach((child: NavigationChild) => {
              if (child.name.toLowerCase() == navData.driverExternalTrainingReport.idName.toLowerCase()) {
                child.hidden = false;
              }
            });
          } else if (navigation.name.toLowerCase() == navData.driverIndex.idName.toLowerCase()) {
            if (userPermissions.permIndexing == this.permLevels.view) {
              navigation.hidden = false;

              children.forEach((child: NavigationChild) => {
                if (child.name.toLowerCase() == navData.scorecard.idName.toLowerCase()) {
                  child.hidden = false; // Always shown if Indexing permission is 'view'
                } else if (child.name.toLowerCase() == navData.mentorRewards.idName.toLowerCase()) {
                  if (
                    companyPermissions.permMisMenuMentorRewards === this.permLevels.TRUE &&
                    userPermissions.permMisMenuRewards === this.permLevels.TRUE
                  ) {
                    child.hidden = false;
                  }
                } else if (child.name.toLowerCase() == navData.mentorByEdriving.idName.toLowerCase()) {
                  if (
                    companyPermissions.permIsMentor &&
                    companyPermissions.permMisMenuMentorVisible == this.permLevels.TRUE
                  ) {
                    child.hidden = false;
                  }
                } else if (child.name.toLowerCase() == navData.eventsTable.idName.toLowerCase()) {
                  if (
                    companyPermissions.permMisMenuDriverIndexEventsTableVisible == this.permLevels.TRUE &&
                    userPermissions.permEventsTable.toLowerCase() !== this.permLevels.none
                  ) {
                    child.hidden = false;
                  }
                } else if (child.name.toLowerCase() == navData.riskLevelChanges.idName.toLowerCase()) {
                  if (companyPermissions.permMisMenuRiskLevelChanges === this.permLevels.TRUE) {
                    child.hidden = false;
                  }
                } else if (child.name.toLowerCase() === navData.universe.idName.toLowerCase()) {
                  if (companyPermissions.permMisMenuDriverIndexUniverseVisible === this.permLevels.TRUE) {
                    child.hidden = this.prodOrPreProdInUse;
                  }
                } else if (child.name.toLowerCase() === navData.incidentsCollisions.idName.toLowerCase()) {
                  if (userPermissions.permIncidentsCollisions === this.permLevels.TRUE) {
                    child.hidden = false;
                  }
                } else if (child.name.toLowerCase() === navData.compare.idName.toLowerCase()) {
                  if (companyPermissions.permMisMenuDriverIndexCompareVisible === this.permLevels.TRUE) {
                    child.hidden = false;
                  }
                } else if (child.name.toLowerCase() === navData.distanceTravelled.idName.toLowerCase()) {
                  if (companyPermissions.permMenuDistanceTravelledReport === this.permLevels.TRUE) {
                    child.hidden = false;
                  }
                } else if (child.name.toLowerCase() === navData.workHours.idName.toLowerCase()) {
                  if (
                    companyPermissions.permMisWorkHoursReport === this.permLevels.TRUE &&
                    userPermissions.permWorkHoursReport === this.permLevels.TRUE
                  ) {
                    child.hidden = false;
                  }
                }
              });
            }
          } else if (navigation.name.toLowerCase() == navData.coaching.idName.toLowerCase()) {
            children.forEach((child: NavigationChild) => {
              if (child.name.toLowerCase() === navData.coachingCompletions.idName.toLowerCase()) {
                if (userPermissions.permCoachingCompletions === this.permLevels.TRUE) {
                  child.hidden = false;
                }
              }

              if (child.name.toLowerCase() === navData.amazonCoachingAssignments.idName.toLowerCase()) {
                if (
                  companyPermissions.permMenuAmazonMentorCoachReport === this.permLevels.TRUE &&
                  userPermissions.permAmazonMentorCoachReport === this.permLevels.TRUE
                ) {
                  child.hidden = false;
                }
              }
            });
            if (children.find((child: NavigationChild) => child.hidden === false)) {
              navigation.hidden = false;
            }
          } else if (navigation.name.toLowerCase() == navData.coachingRecommendations.idName.toLowerCase()) {
            if (this.userProfile.recommendationInterventions) {
              children.forEach((child: NavigationChild) => {
                // OneToOne
                if (child.name.toLowerCase() === navData.OneToOne.idName.toLowerCase()) {
                  if (this.userProfile.recommendationInterventions.includes(CoachingInterventions.oneToOne)) {
                    child.hidden = false;
                  }
                }

                // OneToOne Pro Coach
                if (child.name.toLowerCase() === navData.OneToOneProCoach.idName.toLowerCase()) {
                  if (this.userProfile.recommendationInterventions.includes(CoachingInterventions.oneToOneProCoach)) {
                    child.hidden = false;
                  }
                }

                // PCC
                if (child.name.toLowerCase() === navData.PccReview.idName.toLowerCase()) {
                  if (this.userProfile.recommendationInterventions.includes(CoachingInterventions.pccReview)) {
                    child.hidden = false;
                  }
                }

                // PCC Pro Coach
                if (child.name.toLowerCase() === navData.PccReviewProCoach.idName.toLowerCase()) {
                  if (this.userProfile.recommendationInterventions.includes(CoachingInterventions.pccReviewProCoach)) {
                    child.hidden = false;
                  }
                }

                // DriverIndex Coach
                if (child.name.toLowerCase() === navData.DriverImprovementReview.idName.toLowerCase()) {
                  if (
                    this.userProfile.recommendationInterventions.includes(CoachingInterventions.driverImprovementReview)
                  ) {
                    child.hidden = false;
                  }
                }

                // DriverIndex Pro Coach
                if (child.name.toLowerCase() === navData.DriverIndexProCoach.idName.toLowerCase()) {
                  if (
                    this.userProfile.recommendationInterventions.includes(CoachingInterventions.driverIndexProCoach)
                  ) {
                    child.hidden = false;
                  }
                }

                // 1 Week FICO® <559
                if (child.name.toLowerCase() === navData.OneWeekFICO_LT500.idName.toLowerCase()) {
                  if (
                    this.userProfile.recommendationInterventions.includes(
                      CoachingInterventions.amzlWeeklyDriverIndexCoach
                    )
                  ) {
                    child.hidden = false;
                  }
                }

                // 2 Weeks FICO® <710
                if (child.name.toLowerCase() === navData.TwoWeeksFICO_LT710.idName.toLowerCase()) {
                  if (
                    this.userProfile.recommendationInterventions.includes(
                      CoachingInterventions.amzlTwoWeeklyDriverIndexCoach
                    )
                  ) {
                    child.hidden = false;
                  }
                }
              });
            }

            if (
              children.find((child: NavigationChild) => child.hidden === false) &&
              userPermissions.permCoachingRecommendations.toLowerCase() === this.permLevels.view
            ) {
              navigation.hidden = false;
            }
          } else if (navigation.name.toLowerCase() == navData.mvrPlus.idName.toLowerCase()) {
            const mvrPermission = userPermissions.permUsMvr;
            children.forEach((child: NavigationChild) => {
              if (mvrPermission && mvrPermission.toLowerCase() !== this.permLevels.none) {
                // DRM
                if (child.name.toLowerCase() === navData.drm.idName.toLowerCase()) {
                  if (companyPermissions.permDrm === this.permLevels.TRUE) {
                    child.hidden = false;
                  }
                }

                // Main & Admin
                if (
                  child.name.toLowerCase() === navData.main.idName.toLowerCase() ||
                  child.name.toLowerCase() === navData.administration.idName.toLowerCase()
                ) {
                  child.hidden = false;
                }
              }
            });

            if (children.find((childNav) => childNav.hidden === false)) {
              navigation.hidden = false;
            }
          } else if (navigation.name.toLowerCase() == navData.mentorTspSummary.idName.toLowerCase()) {
            if (companyPermissions.permMisMenuMentorTsp == this.permLevels.TRUE) {
              navigation.hidden = false;
            }
          } else if (
            navigation.name.toLowerCase() == navData.vrmAlerts.idName.toLowerCase() &&
            userPermissions.permVrmAlerts.toLowerCase() !== this.permLevels.none
          ) {
            if (companyPermissions.permMisAlerts == this.permLevels.TRUE) {
              navigation.hidden = false;
            }
          } else if (navigation.name.toLowerCase() == navData.driverPerformance.idName.toLowerCase()) {
            children.forEach((child: NavigationChild) => {
              if (
                (child.name.toLowerCase() === navData.driverPerformanceReport.idName.toLowerCase() ||
                  child.name.toLowerCase() === navData.driverPerformanceComparison.idName.toLowerCase()) &&
                companyPermissions.permMisMenuDriverPerformance == this.permLevels.TRUE &&
                userPermissions.permDriverPerformance == this.permLevels.TRUE
              ) {
                child.hidden = false;
              }

              if (
                child.name.toLowerCase() === navData.mentorUtilisationReport.idName.toLowerCase() &&
                companyPermissions.permMisMenuMentorUtilisationReport == this.permLevels.TRUE &&
                userPermissions.permMentorUtilisationReport == this.permLevels.TRUE
              ) {
                child.hidden = false;
              }

              if (children.find((childNav) => childNav.hidden === false)) {
                navigation.hidden = false;
              }
            });
          } else if (navigation.name.toLowerCase() == navData.stationReport.idName.toLowerCase()) {
            if (
              companyPermissions.permMisMenuStationPerformance == this.permLevels.TRUE &&
              this.userProfile.distributorName !== 'amzl_der_pilot'
            ) {
              navigation.hidden = false;
            }
          } else if (navigation.name.toLowerCase() === navData.guidesFaq.idName.toLowerCase()) {
            if (companyPermissions.permMisMenuFaq == this.permLevels.TRUE) {
              navigation.hidden = false;
              children.forEach((child: NavigationChild) => {
                if (child.name.toLowerCase() === navData.mentorFaqExternalLink.idName.toLowerCase()) {
                  if (companyPermissions.permMisMenuExternalLinkMentorFaq === this.permLevels.TRUE) {
                    child.hidden = false;
                  }
                }
              });
            }

            children.forEach((child: NavigationChild) => {
              if (child.name.toLowerCase() === navData.mentorFaqExternalLink.idName.toLowerCase()) {
                if (companyPermissions.permMisMenuExternalLinkMentorFaq === this.permLevels.TRUE) {
                  child.hidden = false;
                }
              }
            });
          } else if (
            navigation.name.toLowerCase() === navData.managerTools.idName.toLowerCase() &&
            !this.managerToolsHidden()
          ) {
            navigation.hidden = false;

            children.forEach((child: NavigationChild) => {
              if (child.name.toLowerCase() == navData.misUsers.idName.toLowerCase() && !this.misUsersHidden()) {
                child.hidden = false;
              }

              if (
                child.name.toLowerCase() == navData.managerActivityReport.idName.toLowerCase() &&
                !this.managerActivityReportHidden()
              ) {
                child.hidden = false;
              }

              if (
                child.name.toLowerCase() === navData.customInformationContent.idName.toLowerCase() &&
                !this.customContentHidden()
              ) {
                child.hidden = false;
              }

              if (child.name.toLowerCase() == navData.driverList.idName.toLowerCase() && !this.isDriverListHidden()) {
                child.hidden = false;
              }
            });
          } else if (
            navigation.name.toLowerCase() === navData.misNotifications.idName.toLowerCase() &&
            !this.misNotificationsHidden()
          ) {
            navigation.hidden = false;
          } else if (navigation.name.toLowerCase() === navData.contactUs.idName.toLowerCase()) {
            if (companyPermissions.permMisMenuContactUs == this.permLevels.TRUE) {
              navigation.hidden = false;
            }
          } else if (navigation.name.toLowerCase() === navData.threeSixty.idName.toLowerCase()) {
            if (companyPermissions.permMisMenu360 == this.permLevels.TRUE) {
              navigation.hidden = false;
            }
          } else if (navigation.name.toLowerCase() === navData.licenceChecking.idName.toLowerCase()) {
            if (userPermissions.permUkLicense !== this.permLevels.none) {
              navigation.hidden = false;
            }
          } else if (navigation.name.toLowerCase() === navData.driverImport.idName.toLowerCase()) {
            if (companyPermissions.permMisMenuExecuteFileImport === this.permLevels.TRUE) {
              navigation.hidden = false;
            }
          }
        });

        this.navigation.forEach((navigation) => {
          const children = navigation.children;

          // Non AMZL Companies | NOTE: Some of these are Amazon but not under the AMZL dist-name. E.g. Amazon Japan
          if (navigation.name.toLowerCase() == navData.statusReport.idName.toLowerCase()) {
            children.forEach((child: NavigationChild) => {
              if (child.name.toLowerCase() === navData.customReports.idName.toLowerCase()) {
                if (userPermissions.permCustomReports === this.permLevels.view) {
                  child.hidden = false;
                }
              } else if (child?.isVrmAnalyticsDashboard) {
                child.hidden = !this.canAccessDashboard(child.id);
              }
            });

            const hasChildNav = children.find((childNav) => childNav.hidden === false);
            if (hasChildNav) {
              navigation.hidden = false;
            }
          }
        });
      }

      this.triggerNavigationReadyObservable();
    }
  }

  public managerToolsHidden(): boolean {
    let hidden = true;
    if (
      !this.misUsersHidden() ||
      !this.managerActivityReportHidden() ||
      !this.customContentHidden() ||
      !this.isDriverListHidden()
    ) {
      hidden = false;
    }

    return hidden;
  }

  public misUsersHidden(): boolean {
    const userPermissions = this.userProfile.userPermissions;
    let hidden = true;

    if (userPermissions.permMISUsers === this.permLevels.TRUE) {
      hidden = false;
    }

    return hidden;
  }

  public managerActivityReportHidden(): boolean {
    const userPermissions = this.userProfile.userPermissions;
    let hidden = true;

    if (userPermissions.permManagerActivityReport === this.permLevels.TRUE) {
      hidden = false;
    }

    return hidden;
  }

  public customContentHidden(): boolean {
    const distributorPermissions = this.userProfile.distributorPermissions;
    const companyPermissions = this.userProfile.companyPermissions;
    const userPermissions = this.userProfile.userPermissions;
    let hidden = true;

    if (
      (distributorPermissions && distributorPermissions.permMisMenuCustomContent === this.permLevels.TRUE) ||
      (companyPermissions.permMenuCustomContent == this.permLevels.TRUE &&
        (userPermissions.permCustomContent == this.permLevels.view ||
          userPermissions.permCustomContent == this.permLevels.viewEdit))
    ) {
      hidden = false;
    }

    return hidden;
  }

  public isDriverListHidden() {
    const userPermissions = this.userProfile.userPermissions;
    let hidden = true;

    if (
      userPermissions.permDriverList === this.permLevels.view ||
      userPermissions.permDriverList === this.permLevels.viewEdit
    ) {
      hidden = false;
    }

    return hidden;
  }

  public misNotificationsHidden(): boolean {
    const distributorPermissions = this.userProfile.distributorPermissions;
    const companyPermissions = this.userProfile.companyPermissions;
    const userPermissions = this.userProfile.userPermissions;
    let hidden = true;

    if (
      (distributorPermissions && distributorPermissions.permMisMenuMisNotifications === this.permLevels.TRUE) ||
      (companyPermissions.permMenuMisNotifications == this.permLevels.TRUE &&
        (userPermissions.permMISNotifications == this.permLevels.view ||
          userPermissions.permMISNotifications == this.permLevels.viewEdit))
    ) {
      hidden = false;
    }

    return hidden;
  }

  public mvrPageHidden(): boolean {
    let hidden = true;

    if (
      this.userProfile.userPermissions.permUsMvr &&
      this.userProfile.userPermissions.permUsMvr.toLowerCase() !== this.permLevels.none
    ) {
      hidden = false;
    }

    return hidden;
  }

  public licenceCheckingIsHidden(): boolean {
    let hidden: boolean = true;

    if (this.userProfile.userPermissions.permUkLicense !== this.permLevels.none) {
      hidden = false;
    }

    return hidden;
  }

  private hasCourseCode(course: any, navigation: Navigation | NavigationChild): boolean {
    let hidden: boolean = true;
    const courseKeys = Object.keys(course);

    for (let i = 0; i < courseKeys.length; i++) {
      const courseKey = courseKeys[i];

      if (navigation.name.toLowerCase() === course[courseKey].name.toLowerCase()) {
        for (let x = 0; x < course[courseKey].codes.length; x++) {
          const courseCode = course[courseKey].codes[x];
          if (this.userProfile.courseList.includes(courseCode)) {
            hidden = false;
            break;
          }
        }

        break;
      }
    }

    return hidden;
  }

  private hasCourseCodeInUse(course: any, navigation: Navigation | NavigationChild): boolean {
    let hidden: boolean = true;
    const courseKeys = Object.keys(course);

    for (let i = 0; i < courseKeys.length; i++) {
      const courseKey = courseKeys[i];

      if (navigation.name.toLowerCase() === course[courseKey].name.toLowerCase()) {
        for (let x = 0; x < course[courseKey].codes.length; x++) {
          const courseCode = course[courseKey].codes[x];
          if (this.userProfile.coursesInUse.includes(courseCode)) {
            hidden = false;
            break;
          }
        }

        break;
      }
    }

    return hidden;
  }

  private getCustomName(navdataIdName: string) {
    let customMenu = null;

    if (this.userProfile && this.userProfile.customMenuLabels) {
      customMenu = this.userProfile.customMenuLabels.filter((item: CustomMenuItem) => {
        return item.navdataIdName === navdataIdName;
      });
    }

    return customMenu && customMenu.length ? customMenu[0].customName : null;
  }

  public getNavTitle(navItem: any): string {
    return navItem.customName
      ? navItem.customName
      : navItem.translatedName
      ? navItem.translatedName
      : navItem.fallbackName;
  }

  public getSelectedNav() {
    setTimeout(() => {
      for (const navItem of this.navigation) {
        if (navItem.selected && !navItem.children) {
          this.selectedNav = navItem;
          break;
        } else {
          if (navItem.children && navItem.children.length) {
            for (const navItemChild of navItem.children) {
              if (navItemChild.selected) {
                this.selectedNav = navItemChild;
                break;
              }
            }
          }
        }
      }

      console.log(this.selectedNav);
    }, 1000);
  }

  public passwordMismatch(currentPass: string, newPass: string): boolean {
    const mismatch = currentPass !== newPass;
    return mismatch;
  }

  public convertMinToSeconds(minutes: number): number {
    // Must never return zero or less
    const minimumSeconds = 10;
    const convertedSeconds = minutes * 60;

    return convertedSeconds <= 0 ? minimumSeconds : convertedSeconds;
  }

  public convertSecondsToMilliseconds(seconds: number): number {
    let convertedMilliseconds = 0;

    if (seconds > 0) {
      convertedMilliseconds = seconds * 1000;
    }

    return convertedMilliseconds;
  }

  /**
   * @param uri URI string with :placeholders
   * @param resolveFrom object Object to resolve :placeholder from
   ** resolves uri with :placeholders
   * */
  public resolveUriSegments(uri: string, resolveFrom: object): { success: boolean; uri: string } {
    const matches = uri.match(/:(\w+)/g);
    let uriSegment = null;

    for (const match of matches) {
      const substitute = resolveFrom[match.slice(1)];
      if (!substitute) {
        uriSegment = { success: false, uri: uri };
      }
      uri = uri.replace(match, substitute);
    }

    uriSegment = { success: true, uri: uri };
    return uriSegment;
  }

  public processAndLoadDynamicScriptsOnDemand(scriptTarget: DynamicScriptTargetTypes) {
    dynamicScripts.forEach((dScript: DynamicScript) => {
      if (dScript && scriptTarget === dScript.specificTarget && !renderedScriptSrc.includes(dScript.src)) {
        const genericScriptEl = document.createElement('script');
        genericScriptEl.type = dScript.type ? dScript.type : 'text/javascript';
        genericScriptEl.async = dScript.async ? dScript.async : false;

        if (dScript.isExternal && dScript.src) {
          // Apply script with source
          const scriptEl = genericScriptEl;
          scriptEl.src = dScript.src;

          if (dScript.dataProps && dScript.dataProps.length) {
            dScript.dataProps.forEach((dataProp: DataProp) => {
              if (dataProp.name && dataProp.value) {
                scriptEl.setAttribute(`data-${dataProp.name.replace('data-', '')}`, dataProp.value);
              }
            });
          }

          document.getElementsByTagName('head')[0].appendChild(scriptEl);
          renderedScriptSrc.push(dScript.src);
        } else {
          if (dScript.shortScript) {
            // Apply short script
            const scriptEl = genericScriptEl;
            scriptEl.innerText = dScript.shortScript;

            document.getElementsByTagName('head')[0].appendChild(scriptEl);
            renderedScriptSrc.push(dScript.src);
          } else {
            console.warn('::: Script ignored as not correctly configured', dScript);
          }
        }
      }
    });
  }

  /**
   * Used to replace _.keyBy as lodash has been removed
   * @param arrayOfObjects
   * Input must be an array containing objects: {}
   *
   * @param key
   * string or numeric value to use when creating new object property
   *
   * @returns
   * will always return an object. Empty object if data is empty or invalid
   */
  public arrayToKeyedObject(arrayOfObjects: Array<{}>, key: string | number): {} {
    const incompatibleItems: Array<any> = [];
    const converted = {};

    if (arrayOfObjects && key) {
      if (arrayOfObjects.length) {
        arrayOfObjects.forEach((item) => {
          if (item[key]) {
            const itemKey = item[key];
            converted[itemKey] = item;
          } else {
            incompatibleItems.push(item);
          }
        });

        if (incompatibleItems.length) {
          console.warn('The following items were not compatible for conversion', incompatibleItems);
        }
      }
    } else {
      console.warn('ERROR: Invalid data provided for Array to Object conversion');
    }

    return converted;
  }

  /**
   * @param value Value to be displayed as percentage.
   * decimal will be removed.
   */
  public displayNumberAsPercentage(value: any, reportName?: string): string {
    let convertedValue = value;
    const reportsUsingPercentagesWithDecimals = [
      'station performance report',
      'station performance delta report',
      'mentor driver performance report',
      'mentor driver delta performance report',
    ];

    if (value && !isNaN(value)) {
      if (reportName && reportsUsingPercentagesWithDecimals.includes(reportName.toLowerCase())) {
        convertedValue = value + '%';
      } else {
        convertedValue = value.toString().includes('.') ? Number(value).toFixed(0) + '%' : value + '%';
      }
    }

    return convertedValue;
  }

  /**
   * @param value Value to be converted to decimal
   * @param decimalPlaces Optional Decimal places, defaults to 2 if already has more than 2 decimals.
   * Otherwise, it'll default to 1 for data already returning 1 decimal place.
   */
  public numberToDecimalPlace(value: any, decimalPlaces: number = 2) {
    let convertedValue = value;

    if ((value != null || value != undefined) && !isNaN(value)) {
      convertedValue = Number(value).toFixed(decimalPlaces);
    }

    return convertedValue;
  }

  public prefixNumberWithPlusSign(value: number, hasDecimal: boolean): any {
    let decimalValue: string;
    let finalValue: any = value;

    if (typeof value == 'number' && value != null && value != undefined) {
      if (hasDecimal) {
        decimalValue = this.numberToDecimalPlace(value);
        finalValue = value > 0 ? '+' + decimalValue : decimalValue;
      } else {
        finalValue = value > 0 ? '+' + value : value;
      }
    }

    return finalValue;
  }

  /**
   * @deprecated If using RatingToTextType.highMediumLowNa, use convertRatingScoreToRatingText from driver.helpers.ts instead.
   * @param ratingType
   * @param value
   */
  public mapNumericRatingToText(ratingType: RatingToTextType, value: number): string {
    let translatedMappedRatingText = this.readyTranslations.reportData.na;

    if (!isNaN(value)) {
      if (ratingType === NumericRatingToTextEnum.highMediumLowNa) {
        if (value === this.highMediumLowRatingValues.veryHigh) {
          translatedMappedRatingText = this.readyTranslations.reportData.veryHigh;
        }

        if (value === this.highMediumLowRatingValues.high) {
          translatedMappedRatingText = this.readyTranslations.reportData.high;
        }

        if (value === this.highMediumLowRatingValues.medium) {
          translatedMappedRatingText = this.readyTranslations.reportData.medium;
        }

        if (value === this.highMediumLowRatingValues.low) {
          translatedMappedRatingText = this.readyTranslations.reportData.low;
        }
      } else if (ratingType === NumericRatingToTextEnum.poorAverageGoodNa) {
        if (value === 0) {
          translatedMappedRatingText = this.readyTranslations.reportData.noData;
        } else if (value <= this.mentorDrivingRatingValues.highRisk.max) {
          translatedMappedRatingText = this.readyTranslations.reportData.highRisk;
        } else if (
          value >= this.mentorDrivingRatingValues.mediumRisk.min &&
          value <= this.mentorDrivingRatingValues.mediumRisk.max
        ) {
          translatedMappedRatingText = this.readyTranslations.reportData.mediumRisk;
        } else {
          translatedMappedRatingText = this.readyTranslations.reportData.lowRisk;
        }
      } else if (ratingType === NumericRatingToTextEnum.completeIncompleteNotStartedNa) {
        if (value === this.completeIncompleteNotStartedNa.notStarted) {
          translatedMappedRatingText = this.readyTranslations.reportData.notStarted;
        }

        if (value === this.completeIncompleteNotStartedNa.incomplete) {
          translatedMappedRatingText = this.readyTranslations.reportData.incomplete;
        }

        if (value === this.completeIncompleteNotStartedNa.complete) {
          translatedMappedRatingText = this.readyTranslations.reportData.complete;
        }
      } else {
        console.error('VRM ERROR: NumericToTextRating mapping not supported');
      }
    }

    return translatedMappedRatingText;
  }

  public predictMinWidthByColName(columnName: string): number {
    let predictedWidth: number = null;

    if (columnName) {
      const colNameWords: Array<string> = columnName.split(' ');
      let pixelPerChar: number;

      const charCount: number =
        colNameWords.length > 1 ? this.getLongestWord(colNameWords).length : columnName.split('').length;

      pixelPerChar = charCount > 8 ? 11.5 : 15;
      predictedWidth = charCount * pixelPerChar;
    }

    return predictedWidth;
  }

  public predictMinWidthInPixelByWord(wordOrPhrase: string): number {
    let predictedWidth: number;
    const pixelPerChar: number = 11;
    const wordArray = wordOrPhrase.split(' ');
    const charCount: number = wordArray.length > 1 ? this.getLongestWord(wordArray).length : wordOrPhrase.length;

    predictedWidth = charCount * pixelPerChar;

    return predictedWidth;
  }

  private getLongestWord(words: Array<string>): string {
    let longestWord: string;

    words.forEach((word: string, key: number) => {
      const nextKey: number = key + 1;
      const currentWordLength: number = word ? word.split('').length : 0;
      const nextWordLength: number = words[key + 1] ? words[nextKey].length : 0;
      let cachedLongestWord: string;

      if (nextWordLength > currentWordLength) {
        cachedLongestWord = words[nextKey];
      } else {
        cachedLongestWord = word;
      }

      if (longestWord) {
        if (cachedLongestWord.split('').length > longestWord.split('').length) {
          longestWord = cachedLongestWord;
        }
      } else {
        longestWord = cachedLongestWord;
      }
    });

    return longestWord;
  }

  public getInitialFromNames(names: string): string {
    let initial: string;

    const nameArray: Array<string> = names.split(' ');
    if (nameArray.length > 1) {
      initial = nameArray[0].substring(0, 1) + nameArray[1].substring(0, 1);
    } else {
      initial = names.substring(0, 1);
    }

    return initial.toUpperCase();
  }

  public toTitleCase(text: string): string {
    const words = text.split(' ');
    let titleText = '';
    words.forEach((word: string) => {
      const titleCaseWord = this.wordToTitleCase(word);
      titleText += titleCaseWord + ' ';
    });

    return titleText.trim();
  }

  private wordToTitleCase(word: string) {
    const characters = word.split('');
    let titleCase = '';
    characters.forEach((char: string, key: number) => {
      char = key === 0 ? char.toUpperCase() : char.toLowerCase();
      titleCase += char;
    });

    return titleCase;
  }

  public toVrmCaps(sentence: string): string {
    let modifiedSentence = sentence;
    const vrmCapitalWords = [
      'VRM',
      'DriverINDEX',
      'MVR',
      'MVR+',
      'RoadRISK',
      'DRM',
      'FAQ',
      'LLC',
      'DSP',
      'DVCR',
      'eDriving',
    ];

    if (sentence) {
      vrmCapitalWords.forEach((vrmCap) => {
        if (sentence.toLowerCase().includes(vrmCap.toLowerCase())) {
          const wordSearch = new RegExp(vrmCap, 'gi');
          modifiedSentence = sentence.replace(wordSearch, vrmCap);
        }
      });
    }

    return modifiedSentence;
  }

  public replaceSpecialChars(text: string): string {
    let finalText = text;
    const specialChars: Array<RegExp> = [
      /</,
      />/,
      /:/,
      /"/,
      /`/,
      /'/,
      /\//,
      /\\/,
      /\|/,
      /\?/,
      /\*/,
      /\$/,
      /£/,
      /;/,
      /@/,
    ];

    specialChars.forEach((specialChar: RegExp) => {
      if (specialChar.source === new RegExp(/</).source || specialChar.source === new RegExp(/>/).source) {
        finalText = finalText.replace(/</g, '_lt_').replace(/>/g, '_gt_');
      } else {
        const sChar = new RegExp(specialChar, 'g');
        finalText = finalText.replace(sChar, '_');
      }
    });

    return finalText;
  }

  public convertTruthyFalsyPermToBoolean(permOrSetting: string): boolean {
    let permOutput: boolean = false;

    if (permOrSetting) {
      if (permOrSetting === this.permLevels.TRUE || permOrSetting === this.permLevels.yes) {
        permOutput = true;
      }
    }

    return permOutput;
  }

  public getApplicableIconForExtension(extension: string): VrmFileIcon {
    const ext = extension ? extension.trim().replace(/\./g, '') : '';
    const unknownFile: VrmFileIcon = { icon: 'far fa-file' };
    let fileIcon: VrmFileIcon = unknownFile;

    if (extension) {
      fileIcon = this.fileTypesToIcon[ext];

      if (!fileIcon) {
        fileIcon = unknownFile;
      }
    }

    return fileIcon;
  }

  printContent(printSectionElementRef: ElementRef, printTitle?: string, callback?: () => void) {
    const delay = 250;

    if (printSectionElementRef) {
      setTimeout(() => {
        const content = printSectionElementRef.nativeElement.innerHTML;
        const printWindow = window.open('', 'Print');

        printWindow.document.write(`
          <html>
            <head><title>${printTitle}</title></head>
            <body style="font-family: Roboto, Helvetica Neue, sans-serif">${content}</body>
          </html>
        `);

        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
        printWindow.close();

        setTimeout(() => {
          if (callback) {
            callback();
          }
        }, delay);
      }, delay);
    }

    return true;
  }

  navigateNext(currentIndex: number, maxIndex: number): number {
    let newIndex = currentIndex;

    if (currentIndex !== maxIndex) {
      newIndex++;
    }

    return newIndex;
  }

  navigatePrev(currentIndex: number, minIndex: number = 0): number {
    let newIndex = currentIndex;

    if (currentIndex !== minIndex) {
      newIndex--;
    }

    return newIndex;
  }

  public getTextFromNavigationFilterTexts(text: string): string {
    return this.navigationFilterTexts[text]
      ? this.navigationFilterTexts[text].translatedName
        ? this.navigationFilterTexts[text].translatedName
        : this.navigationFilterTexts[text].name
      : text;
  }

  /**
   * NOTE
   * If calling the method, ensure that it is also called onDestroy with pauseOrDestroy set to true.
   */
  carouselDriver(listLength: number, pausedOrDestroy: boolean = false, delayInMs: number = 12000) {
    this.carouselItemIndicatorList = new Array(listLength);
    this.carouselPaused = pausedOrDestroy;
    const boundary = listLength - 1;

    if (!pausedOrDestroy) {
      this.appStableSinceInitLoad$.pipe(first((stable) => stable)).subscribe({
        next: (appStatus) =>
          this.zone.run(() => {
            this.carouselIntervalInterrupter = setInterval(() => {
              if (this.carouselItemIndex >= boundary) {
                this.carouselItemIndex = 0;
              } else {
                this.carouselItemIndex++;
              }
            }, delayInMs);
          }),
      });
    } else {
      if (this.carouselIntervalInterrupter) {
        clearInterval(this.carouselIntervalInterrupter);
      }
    }
  }

  trackByFn(index: number) {
    return index;
  }

  // Common requests
  commonGet(secondaryPath: string): Observable<any> {
    return this.http.get(`${Config.apiRoot.uri + secondaryPath}`).pipe(
      map((response) => {
        console.log(`%c GET Endpoint: ${secondaryPath}`, 'color: lightblue', response);
        return response;
      })
    );
  }

  commonPost(secondaryPath: string, postData?: any, vrmRequestName: string = ''): Observable<any> {
    return this.http.post(`${Config.apiRoot.uri + secondaryPath}`, postData).pipe(
      map((response) => {
        console.log(`%c POST Endpoint: ${secondaryPath}`, 'color: green', response);
        return response;
      })
    );
  }

  commonPut(secondaryPath: string, postData?: any, vrmRequestName: string = ''): Observable<any> {
    return this.http.put(`${Config.apiRoot.uri + secondaryPath}`, postData).pipe(
      map((response) => {
        console.log(`%c PUT Endpoint: ${secondaryPath}`, 'color: green', response);
        return response;
      })
    );
  }

  commonPatch(secondaryPath: string, postData?: any, vrmRequestName: string = ''): Observable<any> {
    return this.http.patch(`${Config.apiRoot.uri + secondaryPath}`, postData).pipe(
      map((response) => {
        console.log(`%c PATCH Endpoint: ${secondaryPath}`, 'color: green', response);
        return response;
      })
    );
  }

  getLanguageList(): Observable<any> {
    return this.http
      .get(`${Config.languageAssetCdnRoot}vrm-language-list.json?23476832`, {
        headers: new HttpHeaders({
          'ngsw-bypass': 'true',
        }),
      })
      .pipe(
        map((response: { vrmLanguages: Array<any> }) => {
          this.vrmLanguages = response.vrmLanguages;
          return response;
        })
      );
  }

  commonDelete(secondaryPath: string): Observable<any> {
    return this.http.delete(`${Config.apiRoot.uri + secondaryPath}`).pipe(
      map((response) => {
        console.log(`%c DELETE Endpoint: ${secondaryPath}`, 'color: green', response);
        return response;
      })
    );
  }

  /**
   * Returns all the categories from the server.
   */
  public getLicenceCategories(): void {
    if (!this.licenceCategories) {
      this.licenceCategories = [];

      const subscription = this.http
        .get(`${Config.apiRoot.uri}licence/categories`)
        .subscribe((response: { data: Array<any> }) => {
          this.licenceCategories = response.data;
          subscription.unsubscribe();
        });
    }
  }

  /**
   * Returns a category with the matching code.
   *
   * @param code The code for the target category.
   */
  public getLicenceCategoryForCode(code: string): LicenceCategory | null {
    let categories = null;

    if (this.licenceCategories) {
      categories = this.licenceCategories.find((category) => category.code === code);
    }

    return categories;
  }

  /**
   * For the GET requests that require special response type like blob
   */
  commonGetWithResponseType(secondaryPath: string, responseType: any): Observable<any> {
    return this.http.get(`${Config.apiRoot.uri + secondaryPath}`, {
      responseType: responseType,
    });
  }

  releaseMultipleSubscriptions(subObject: any) {
    for (const sub in subObject) {
      if (sub) {
        const subscription: Subscription = subObject[sub];

        if (subscription) {
          subscription.unsubscribe();
        }
      }
    }
  }

  /** @deprecated Deprecated in favour of TranslationService->translate */
  public getSingleTranslation(key: string, fallback: string | number | null): any {
    let value;
    const translatedReason = this.translate.instant(key);

    if (translatedReason !== key) {
      value = translatedReason;
    } else {
      value = fallback;
    }

    return value;
  }

  public shouldHidePin() {
    return this.userProfile.isAmazonCompany && !this.userProfile.isIdsUser;
  }

  private canAccessDashboard(id: string): boolean {
    if (this.userProfile.userPermissions.permManagedDashboardAccess === this.permLevels.TRUE) {
      return this.userProfile.dashboards.some((dashboard) => dashboard.identifier === id);
    }

    const requiredMenuItems = dashboardMenuAccessMap[id] ?? null;

    if (!this.userProfile.dashboards.find((dashboard) => dashboard.identifier === id)) {
      return false;
    }

    if (!requiredMenuItems) {
      return false;
    }

    const menuVisibility = requiredMenuItems.map((item) => {
      const menuItems = item.split('|').map((splitMenuItem) => {
        const negatedAccess = splitMenuItem.startsWith('!');
        const menuId = negatedAccess ? splitMenuItem.slice(1) : splitMenuItem;
        const menuItem = this.findMenuItem(menuId);

        if (!menuItem) {
          return false;
        }

        return menuItem.hidden === negatedAccess;
      });

      return menuItems.includes(true);
    });

    return !menuVisibility.includes(false);
  }

  private findMenuItem(itemToFind: string): Navigation | null {
    let menuItem = null;

    this.navigation.forEach((topLevelMenu) => {
      if (topLevelMenu.id === itemToFind) {
        menuItem = topLevelMenu;
        return;
      }

      (topLevelMenu.children ?? []).forEach((childMenu) => {
        if (childMenu.id === itemToFind) {
          menuItem = childMenu;
        }
      });
    });

    return menuItem;
  }
}
